/**
 * Mock data for Financial Portal prototyping
 * Contains realistic Brazilian school data with orders, invoices, and installments
 */

export interface CustomerUser {
  id: string;
  email: string;
  name: string;
  clientCode: string;
  role: "customer" | "backoffice";
  avatar?: string;
  phone: string;
}

export interface OrderItem {
  productCode: string;
  description: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
}

export type StatusType =
  | "Criado"
  | "Pendente"
  | "Processado"
  | "Em Rota"
  | "Entregue"
  | "Cancelado"
  | "Confirmado";

export interface Order {
  orderId: string;
  id: string;
  orderNumber: string;
  clientCode: string;
  clientName: string;
  orderDate: string;
  deliveryDate: string;
  status: "pending" | "processing" | "delivered" | "cancelled" | "processed";
  totalAmount: number;
  items: OrderItem[];
  paymentStatus: string;
  createdAt: string;
  shippingAmount: number;
  portalB2BOrderStatus: StatusType;
  observations?: string;
  financialSituation?: "waiting payment";
  cycleCode?: string | null;
}

export interface Invoice {
  invoiceId: string;
  invoiceNumber: string;
  orderId: string;
  issueDate: string;
  dueDate: string;
  amount: number;
  status: "pending" | "paid" | "overdue" | "cancelled";
  pdfUrl: string;
  xmlUrl: string;
}

export interface Installment {
  installmentId: string;
  orderId: string;
  invoiceId: string;
  installmentNumber: number;
  dueDate: string;
  amount: number;
  status: "pending" | "Em Aberto" | "paid" | "overdue" | "Pago";
  paymentDate?: string;
  boletoUrl: string;
  boletoBarcode: string;
  paymentProofUrl?: string;
}

export interface ActivityLog {
  activityId: string;
  clientCode: string;
  clientName: string;
  actionType:
    | "ORDER_CREATED"
    | "ORDER_UPDATED"
    | "PAYMENT_RECEIVED"
    | "INVOICE_ISSUED"
    | "PRODUCT_ADDED"
    | "PRODUCT_EDITED"
    | "PRODUCT_REMOVED"
    | "ACCOUNT_UPDATED"
    | "CONTACT_MADE";
  timestamp: string;
  description: string;
  actor: string;
  metadata?: Record<string, any>;
}

// Mock Users
export const mockUsers: CustomerUser[] = [
  {
    id: "user-001",
    email: "financeiro@colegiosaopaulo.edu.br",
    name: "Maria Silva Santos",
    clientCode: "CNX001",
    role: "customer",
    phone: "(11) 3456-7890",
  },
  {
    id: "user-002",
    email: "compras@escolavida.com.br",
    name: "João Pedro Oliveira",
    clientCode: "CNX002",
    role: "customer",
    phone: "(21) 2345-6789",
  },
  {
    id: "user-003",
    email: "administrativo@colegioexcelencia.edu.br",
    name: "Ana Carolina Ferreira",
    clientCode: "CNX003",
    role: "customer",
    phone: "(31) 3234-5678",
  },
  {
    id: "user-004",
    email: "financeiro@escolamodelo.edu.br",
    name: "Pedro Henrique Costa",
    clientCode: "CNX004",
    role: "customer",
    phone: "(41) 3567-8901",
  },
  {
    id: "user-005",
    email: "compras@colegiosaber.com.br",
    name: "Juliana Almeida Silva",
    clientCode: "CNX005",
    role: "customer",
    phone: "(51) 3678-9012",
  },
  {
    id: "user-006",
    email: "administrativo@escolafuturo.edu.br",
    name: "Roberto Carlos Martins",
    clientCode: "CNX006",
    role: "customer",
    phone: "(61) 3789-0123",
  },
  {
    id: "user-007",
    email: "financeiro@colegioprogresso.com.br",
    name: "Fernanda Lima Santos",
    clientCode: "CNX007",
    role: "customer",
    phone: "(71) 3890-1234",
  },
  {
    id: "user-008",
    email: "compras@escolahorizonte.edu.br",
    name: "Marcos Antonio Souza",
    clientCode: "CNX008",
    role: "customer",
    phone: "(81) 3901-2345",
  },
  {
    id: "user-backoffice",
    email: "backoffice@editora.com.br",
    name: "Carlos Eduardo Mendes",
    clientCode: "BACK001",
    role: "backoffice",
    phone: "(11) 4567-8900",
  },
];

// Mock Orders
export const mockOrders: Order[] = [
  {
    orderId: "ord-001",
    orderNumber: "PED-2024-001",
    clientCode: "CNX001",
    clientName: "Colégio São Paulo",
    orderDate: "2024-01-15",
    deliveryDate: "2024-02-20",
    status: "delivered",
    totalAmount: 45680.5,
    paymentStatus: "Pago",
    items: [
      {
        productCode: "MAT-EF1-2024",
        description: "Material Didático - Ensino Fundamental I - 2024",
        quantity: 350,
        unitPrice: 89.5,
        totalPrice: 31325.0,
      },
      {
        productCode: "LIV-PORT-5ANO",
        description: "Livro de Português - 5º Ano",
        quantity: 120,
        unitPrice: 119.62,
        totalPrice: 14355.5,
      },
    ],
    observations: "Entrega realizada com sucesso",
  },
  {
    orderId: "ord-002",
    orderNumber: "PED-2024-002",
    clientCode: "CNX002",
    clientName: "Escola Vida Nova",
    orderDate: "2024-02-10",
    deliveryDate: "2024-03-15",
    status: "processing",
    totalAmount: 67890.0,
    paymentStatus: "Parcial",
    items: [
      {
        productCode: "MAT-EM-2024",
        description: "Material Didático - Ensino Médio - 2024",
        quantity: 280,
        unitPrice: 156.75,
        totalPrice: 43890.0,
      },
      {
        productCode: "KIT-LAB-QUI",
        description: "Kit Laboratório de Química",
        quantity: 50,
        unitPrice: 480.0,
        totalPrice: 24000.0,
      },
    ],
  },
  {
    orderId: "ord-003",
    orderNumber: "PED-2024-003",
    clientCode: "CNX003",
    clientName: "Colégio Excelência",
    orderDate: "2024-03-05",
    deliveryDate: "2024-04-10",
    status: "pending",
    totalAmount: 89234.8,
    paymentStatus: "Pendente",
    items: [
      {
        productCode: "MAT-INF-2024",
        description: "Material Didático - Educação Infantil - 2024",
        quantity: 450,
        unitPrice: 67.8,
        totalPrice: 30510.0,
      },
      {
        productCode: "MAT-EF2-2024",
        description: "Material Didático - Ensino Fundamental II - 2024",
        quantity: 320,
        unitPrice: 125.4,
        totalPrice: 40128.0,
      },
      {
        productCode: "LIV-ING-9ANO",
        description: "Livro de Inglês - 9º Ano",
        quantity: 180,
        unitPrice: 103.71,
        totalPrice: 18668.8,
      },
    ],
  },
  {
    orderId: "ord-004",
    orderNumber: "PED-2024-004",
    clientCode: "CNX001",
    clientName: "Colégio São Paulo",
    orderDate: "2024-03-20",
    deliveryDate: "2024-05-01",
    status: "processing",
    totalAmount: 34567.9,
    paymentStatus: "Parcial",
    items: [
      {
        productCode: "LIV-MAT-7ANO",
        description: "Livro de Matemática - 7º Ano",
        quantity: 200,
        unitPrice: 98.45,
        totalPrice: 19690.0,
      },
      {
        productCode: "ATL-GEO",
        description: "Atlas Geográfico Escolar",
        quantity: 150,
        unitPrice: 99.19,
        totalPrice: 14877.9,
      },
    ],
  },
  {
    orderId: "ord-005",
    orderNumber: "PED-2024-005",
    clientCode: "CNX002",
    clientName: "Escola Vida Nova",
    orderDate: "2024-04-12",
    deliveryDate: "2024-05-20",
    status: "cancelled",
    totalAmount: 23450.0,
    paymentStatus: "Cancelado",
    items: [
      {
        productCode: "MAT-EF1-2024",
        description: "Material Didático - Ensino Fundamental I - 2024",
        quantity: 250,
        unitPrice: 93.8,
        totalPrice: 23450.0,
      },
    ],
    observations: "Cancelado a pedido do cliente",
  },
  {
    orderId: "ord-006",
    orderNumber: "PED-2024-006",
    clientCode: "CNX003",
    clientName: "Colégio Excelência",
    orderDate: "2024-05-08",
    deliveryDate: "2024-06-15",
    status: "delivered",
    totalAmount: 52890.4,
    paymentStatus: "Pago",
    items: [
      {
        productCode: "LIV-HIST-2ANO-EM",
        description: "Livro de História - 2º Ano EM",
        quantity: 180,
        unitPrice: 134.5,
        totalPrice: 24210.0,
      },
      {
        productCode: "LIV-BIO-3ANO-EM",
        description: "Livro de Biologia - 3º Ano EM",
        quantity: 160,
        unitPrice: 179.25,
        totalPrice: 28680.4,
      },
    ],
  },
  {
    orderId: "ord-007",
    orderNumber: "PED-2024-007",
    clientCode: "CNX001",
    clientName: "Colégio São Paulo",
    orderDate: "2024-06-15",
    deliveryDate: "2024-07-30",
    status: "processing",
    totalAmount: 78901.2,
    paymentStatus: "Pendente",
    items: [
      {
        productCode: "MAT-EF2-2025",
        description: "Material Didático - Ensino Fundamental II - 2025",
        quantity: 400,
        unitPrice: 132.8,
        totalPrice: 53120.0,
      },
      {
        productCode: "LIV-FIS-1ANO-EM",
        description: "Livro de Física - 1º Ano EM",
        quantity: 170,
        unitPrice: 151.78,
        totalPrice: 25781.2,
      },
    ],
  },
  {
    orderId: "ord-008",
    orderNumber: "PED-2024-008",
    clientCode: "CNX002",
    clientName: "Escola Vida Nova",
    orderDate: "2024-07-22",
    deliveryDate: "2024-09-10",
    status: "pending",
    totalAmount: 91234.5,
    paymentStatus: "Pendente",
    items: [
      {
        productCode: "MAT-EM-2025",
        description: "Material Didático - Ensino Médio - 2025",
        quantity: 320,
        unitPrice: 167.9,
        totalPrice: 53728.0,
      },
      {
        productCode: "KIT-ART",
        description: "Kit Material Artístico Completo",
        quantity: 250,
        unitPrice: 150.03,
        totalPrice: 37506.5,
      },
    ],
  },
  {
    orderId: "ord-009",
    orderNumber: "PED-2024-009",
    clientCode: "CNX003",
    clientName: "Colégio Excelência",
    orderDate: "2024-08-30",
    deliveryDate: "2024-10-15",
    status: "pending",
    totalAmount: 105678.9,
    paymentStatus: "Pendente",
    items: [
      {
        productCode: "MAT-INF-2025",
        description: "Material Didático - Educação Infantil - 2025",
        quantity: 500,
        unitPrice: 72.5,
        totalPrice: 36250.0,
      },
      {
        productCode: "MAT-EF1-2025",
        description: "Material Didático - Ensino Fundamental I - 2025",
        quantity: 420,
        unitPrice: 95.3,
        totalPrice: 40026.0,
      },
      {
        productCode: "LIV-ESP-EM",
        description: "Livro de Espanhol - Ensino Médio",
        quantity: 230,
        unitPrice: 127.95,
        totalPrice: 29428.9,
      },
    ],
  },
  {
    orderId: "ord-010",
    orderNumber: "PED-2024-010",
    clientCode: "CNX001",
    clientName: "Colégio São Paulo",
    orderDate: "2024-09-18",
    deliveryDate: "2024-11-05",
    status: "delivered",
    totalAmount: 62345.7,
    paymentStatus: "Parcial",
    items: [
      {
        productCode: "LIV-QUI-2ANO-EM",
        description: "Livro de Química - 2º Ano EM",
        quantity: 190,
        unitPrice: 189.7,
        totalPrice: 36043.0,
      },
      {
        productCode: "LIV-GEO-EF2",
        description: "Livro de Geografia - Ensino Fundamental II",
        quantity: 220,
        unitPrice: 119.56,
        totalPrice: 26302.7,
      },
    ],
  },
  // CNX004 Orders
  {
    orderId: "ord-011",
    orderNumber: "PED-2024-011",
    clientCode: "CNX004",
    clientName: "Escola Modelo",
    orderDate: "2024-01-20",
    deliveryDate: "2024-02-28",
    status: "delivered",
    totalAmount: 38900.0,
    paymentStatus: "Pago",
    items: [
      {
        productCode: "MAT-EF1-2024",
        description: "Material Didático - Ensino Fundamental I - 2024",
        quantity: 280,
        unitPrice: 89.5,
        totalPrice: 25060.0,
      },
      {
        productCode: "LIV-CIE-5ANO",
        description: "Livro de Ciências - 5º Ano",
        quantity: 130,
        unitPrice: 106.46,
        totalPrice: 13840.0,
      },
    ],
  },
  {
    orderId: "ord-012",
    orderNumber: "PED-2024-012",
    clientCode: "CNX004",
    clientName: "Escola Modelo",
    orderDate: "2024-04-18",
    deliveryDate: "2024-06-05",
    status: "processing",
    totalAmount: 72340.5,
    paymentStatus: "Parcial",
    items: [
      {
        productCode: "MAT-EM-2024",
        description: "Material Didático - Ensino Médio - 2024",
        quantity: 310,
        unitPrice: 156.75,
        totalPrice: 48592.5,
      },
      {
        productCode: "LIV-FIL-EM",
        description: "Livro de Filosofia - Ensino Médio",
        quantity: 180,
        unitPrice: 132.0,
        totalPrice: 23748.0,
      },
    ],
  },
  {
    orderId: "ord-013",
    orderNumber: "PED-2024-013",
    clientCode: "CNX004",
    clientName: "Escola Modelo",
    orderDate: "2024-08-05",
    deliveryDate: "2024-09-20",
    status: "pending",
    totalAmount: 54890.0,
    paymentStatus: "Pendente",
    items: [
      {
        productCode: "MAT-EF2-2024",
        description: "Material Didático - Ensino Fundamental II - 2024",
        quantity: 340,
        unitPrice: 125.4,
        totalPrice: 42636.0,
      },
      {
        productCode: "KIT-ESP",
        description: "Kit Esportivo Escolar",
        quantity: 120,
        unitPrice: 102.12,
        totalPrice: 12254.0,
      },
    ],
  },
  // CNX005 Orders
  {
    orderId: "ord-014",
    orderNumber: "PED-2024-014",
    clientCode: "CNX005",
    clientName: "Colégio Saber",
    orderDate: "2024-02-05",
    deliveryDate: "2024-03-20",
    status: "delivered",
    totalAmount: 96780.0,
    paymentStatus: "Pago",
    items: [
      {
        productCode: "MAT-INF-2024",
        description: "Material Didático - Educação Infantil - 2024",
        quantity: 520,
        unitPrice: 67.8,
        totalPrice: 35256.0,
      },
      {
        productCode: "MAT-EF1-2024",
        description: "Material Didático - Ensino Fundamental I - 2024",
        quantity: 480,
        unitPrice: 89.5,
        totalPrice: 42960.0,
      },
      {
        productCode: "LIV-ART-EF",
        description: "Livro de Arte - Ensino Fundamental",
        quantity: 210,
        unitPrice: 88.4,
        totalPrice: 18564.0,
      },
    ],
  },
  {
    orderId: "ord-015",
    orderNumber: "PED-2024-015",
    clientCode: "CNX005",
    clientName: "Colégio Saber",
    orderDate: "2024-05-12",
    deliveryDate: "2024-07-01",
    status: "delivered",
    totalAmount: 85670.0,
    paymentStatus: "Parcial",
    items: [
      {
        productCode: "MAT-EM-2024",
        description: "Material Didático - Ensino Médio - 2024",
        quantity: 360,
        unitPrice: 156.75,
        totalPrice: 56430.0,
      },
      {
        productCode: "LIV-SOC-EM",
        description: "Livro de Sociologia - Ensino Médio",
        quantity: 240,
        unitPrice: 121.83,
        totalPrice: 29240.0,
      },
    ],
  },
  {
    orderId: "ord-016",
    orderNumber: "PED-2024-016",
    clientCode: "CNX005",
    clientName: "Colégio Saber",
    orderDate: "2024-09-10",
    deliveryDate: "2024-10-30",
    status: "processing",
    totalAmount: 123450.0,
    paymentStatus: "Pendente",
    items: [
      {
        productCode: "MAT-INF-2025",
        description: "Material Didático - Educação Infantil - 2025",
        quantity: 600,
        unitPrice: 72.5,
        totalPrice: 43500.0,
      },
      {
        productCode: "MAT-EF1-2025",
        description: "Material Didático - Ensino Fundamental I - 2025",
        quantity: 550,
        unitPrice: 95.3,
        totalPrice: 52415.0,
      },
      {
        productCode: "MAT-EF2-2025",
        description: "Material Didático - Ensino Fundamental II - 2025",
        quantity: 280,
        unitPrice: 132.8,
        totalPrice: 37184.0,
      },
    ],
  },
  // CNX006 Orders
  {
    orderId: "ord-017",
    orderNumber: "PED-2024-017",
    clientCode: "CNX006",
    clientName: "Escola Futuro",
    orderDate: "2024-03-15",
    deliveryDate: "2024-05-10",
    status: "delivered",
    totalAmount: 67890.0,
    paymentStatus: "Pago",
    items: [
      {
        productCode: "MAT-EF2-2024",
        description: "Material Didático - Ensino Fundamental II - 2024",
        quantity: 380,
        unitPrice: 125.4,
        totalPrice: 47652.0,
      },
      {
        productCode: "LIV-HIST-EF2",
        description: "Livro de História - Ensino Fundamental II",
        quantity: 180,
        unitPrice: 112.43,
        totalPrice: 20238.0,
      },
    ],
  },
  {
    orderId: "ord-018",
    orderNumber: "PED-2024-018",
    clientCode: "CNX006",
    clientName: "Escola Futuro",
    orderDate: "2024-06-20",
    deliveryDate: "2024-08-15",
    status: "processing",
    totalAmount: 98760.0,
    paymentStatus: "Parcial",
    items: [
      {
        productCode: "MAT-EM-2024",
        description: "Material Didático - Ensino Médio - 2024",
        quantity: 420,
        unitPrice: 156.75,
        totalPrice: 65835.0,
      },
      {
        productCode: "KIT-LAB-FIS",
        description: "Kit Laboratório de Física",
        quantity: 65,
        unitPrice: 506.54,
        totalPrice: 32925.0,
      },
    ],
  },
  {
    orderId: "ord-019",
    orderNumber: "PED-2024-019",
    clientCode: "CNX006",
    clientName: "Escola Futuro",
    orderDate: "2024-10-05",
    deliveryDate: "2024-11-25",
    status: "pending",
    totalAmount: 145670.0,
    paymentStatus: "Pendente",
    items: [
      {
        productCode: "MAT-INF-2025",
        description: "Material Didático - Educação Infantil - 2025",
        quantity: 700,
        unitPrice: 72.5,
        totalPrice: 50750.0,
      },
      {
        productCode: "MAT-EF1-2025",
        description: "Material Didático - Ensino Fundamental I - 2025",
        quantity: 620,
        unitPrice: 95.3,
        totalPrice: 59086.0,
      },
      {
        productCode: "MAT-EM-2025",
        description: "Material Didático - Ensino Médio - 2025",
        quantity: 280,
        unitPrice: 167.9,
        totalPrice: 47012.0,
      },
    ],
  },
  // CNX007 Orders
  {
    orderId: "ord-020",
    orderNumber: "PED-2024-020",
    clientCode: "CNX007",
    clientName: "Colégio Progresso",
    orderDate: "2024-02-28",
    deliveryDate: "2024-04-15",
    status: "delivered",
    totalAmount: 43560.0,
    paymentStatus: "Pago",
    items: [
      {
        productCode: "MAT-EF1-2024",
        description: "Material Didático - Ensino Fundamental I - 2024",
        quantity: 320,
        unitPrice: 89.5,
        totalPrice: 28640.0,
      },
      {
        productCode: "LIV-PORT-EF1",
        description: "Livro de Português - Ensino Fundamental I",
        quantity: 140,
        unitPrice: 106.57,
        totalPrice: 14920.0,
      },
    ],
  },
  {
    orderId: "ord-021",
    orderNumber: "PED-2024-021",
    clientCode: "CNX007",
    clientName: "Colégio Progresso",
    orderDate: "2024-07-08",
    deliveryDate: "2024-08-30",
    status: "cancelled",
    totalAmount: 56780.0,
    paymentStatus: "Cancelado",
    items: [
      {
        productCode: "MAT-EF2-2024",
        description: "Material Didático - Ensino Fundamental II - 2024",
        quantity: 400,
        unitPrice: 125.4,
        totalPrice: 50160.0,
      },
      {
        productCode: "KIT-MUS",
        description: "Kit Material Musical",
        quantity: 80,
        unitPrice: 82.75,
        totalPrice: 6620.0,
      },
    ],
    observations: "Cancelado por problemas orçamentários",
  },
  {
    orderId: "ord-022",
    orderNumber: "PED-2024-022",
    clientCode: "CNX007",
    clientName: "Colégio Progresso",
    orderDate: "2024-09-25",
    deliveryDate: "2024-11-10",
    status: "processing",
    totalAmount: 78900.0,
    paymentStatus: "Pendente",
    items: [
      {
        productCode: "MAT-EF1-2025",
        description: "Material Didático - Ensino Fundamental I - 2025",
        quantity: 460,
        unitPrice: 95.3,
        totalPrice: 43838.0,
      },
      {
        productCode: "MAT-EF2-2025",
        description: "Material Didático - Ensino Fundamental II - 2025",
        quantity: 320,
        unitPrice: 132.8,
        totalPrice: 42496.0,
      },
    ],
  },
  // CNX008 Orders
  {
    orderId: "ord-023",
    orderNumber: "PED-2024-023",
    clientCode: "CNX008",
    clientName: "Escola Horizonte",
    orderDate: "2024-01-25",
    deliveryDate: "2024-03-10",
    status: "delivered",
    totalAmount: 52340.0,
    paymentStatus: "Pago",
    items: [
      {
        productCode: "MAT-INF-2024",
        description: "Material Didático - Educação Infantil - 2024",
        quantity: 380,
        unitPrice: 67.8,
        totalPrice: 25764.0,
      },
      {
        productCode: "MAT-EF1-2024",
        description: "Material Didático - Ensino Fundamental I - 2024",
        quantity: 290,
        unitPrice: 89.5,
        totalPrice: 25955.0,
      },
      {
        productCode: "KIT-ART",
        description: "Kit Material Artístico Completo",
        quantity: 40,
        unitPrice: 150.03,
        totalPrice: 6001.0,
      },
    ],
  },
  {
    orderId: "ord-024",
    orderNumber: "PED-2024-024",
    clientCode: "CNX008",
    clientName: "Escola Horizonte",
    orderDate: "2024-05-03",
    deliveryDate: "2024-06-25",
    status: "delivered",
    totalAmount: 89670.0,
    paymentStatus: "Parcial",
    items: [
      {
        productCode: "MAT-EF2-2024",
        description: "Material Didático - Ensino Fundamental II - 2024",
        quantity: 420,
        unitPrice: 125.4,
        totalPrice: 52668.0,
      },
      {
        productCode: "LIV-MAT-EF2",
        description: "Livro de Matemática - Ensino Fundamental II",
        quantity: 280,
        unitPrice: 132.15,
        totalPrice: 37002.0,
      },
    ],
  },
  {
    orderId: "ord-025",
    orderNumber: "PED-2024-025",
    clientCode: "CNX008",
    clientName: "Escola Horizonte",
    orderDate: "2024-08-18",
    deliveryDate: "2024-10-05",
    status: "processing",
    totalAmount: 134560.0,
    paymentStatus: "Pendente",
    items: [
      {
        productCode: "MAT-INF-2025",
        description: "Material Didático - Educação Infantil - 2025",
        quantity: 550,
        unitPrice: 72.5,
        totalPrice: 39875.0,
      },
      {
        productCode: "MAT-EF1-2025",
        description: "Material Didático - Ensino Fundamental I - 2025",
        quantity: 480,
        unitPrice: 95.3,
        totalPrice: 45744.0,
      },
      {
        productCode: "MAT-EF2-2025",
        description: "Material Didático - Ensino Fundamental II - 2025",
        quantity: 450,
        unitPrice: 132.8,
        totalPrice: 59760.0,
      },
    ],
  },
  // Additional orders from existing clients
  {
    orderId: "ord-026",
    orderNumber: "PED-2024-026",
    clientCode: "CNX002",
    clientName: "Escola Vida Nova",
    orderDate: "2024-10-15",
    deliveryDate: "2024-12-01",
    status: "pending",
    totalAmount: 76890.0,
    paymentStatus: "Pendente",
    items: [
      {
        productCode: "MAT-EM-2025",
        description: "Material Didático - Ensino Médio - 2025",
        quantity: 380,
        unitPrice: 167.9,
        totalPrice: 63802.0,
      },
      {
        productCode: "LIV-RED-EM",
        description: "Livro de Redação - Ensino Médio",
        quantity: 120,
        unitPrice: 109.07,
        totalPrice: 13088.0,
      },
    ],
  },
  {
    orderId: "ord-027",
    orderNumber: "PED-2024-027",
    clientCode: "CNX003",
    clientName: "Colégio Excelência",
    orderDate: "2024-11-02",
    deliveryDate: "2024-12-20",
    status: "pending",
    totalAmount: 112340.0,
    paymentStatus: "Pendente",
    items: [
      {
        productCode: "MAT-INF-2025",
        description: "Material Didático - Educação Infantil - 2025",
        quantity: 650,
        unitPrice: 72.5,
        totalPrice: 47125.0,
      },
      {
        productCode: "MAT-EF1-2025",
        description: "Material Didático - Ensino Fundamental I - 2025",
        quantity: 520,
        unitPrice: 95.3,
        totalPrice: 49556.0,
      },
      {
        productCode: "LIV-CIE-EF1",
        description: "Livro de Ciências - Ensino Fundamental I",
        quantity: 180,
        unitPrice: 87.22,
        totalPrice: 15699.0,
      },
    ],
  },
  {
    orderId: "ord-028",
    orderNumber: "PED-2024-028",
    clientCode: "CNX001",
    clientName: "Colégio São Paulo",
    orderDate: "2024-11-10",
    deliveryDate: "2025-01-15",
    status: "pending",
    totalAmount: 98760.0,
    paymentStatus: "Pendente",
    items: [
      {
        productCode: "MAT-EM-2025",
        description: "Material Didático - Ensino Médio - 2025",
        quantity: 440,
        unitPrice: 167.9,
        totalPrice: 73876.0,
      },
      {
        productCode: "KIT-LAB-BIO",
        description: "Kit Laboratório de Biologia",
        quantity: 55,
        unitPrice: 452.53,
        totalPrice: 24889.0,
      },
    ],
  },
  {
    orderId: "ord-029",
    orderNumber: "PED-2024-029",
    clientCode: "CNX004",
    clientName: "Escola Modelo",
    orderDate: "2024-11-15",
    deliveryDate: "2025-01-05",
    status: "pending",
    totalAmount: 67450.0,
    paymentStatus: "Pendente",
    items: [
      {
        productCode: "MAT-EF1-2025",
        description: "Material Didático - Ensino Fundamental I - 2025",
        quantity: 380,
        unitPrice: 95.3,
        totalPrice: 36214.0,
      },
      {
        productCode: "LIV-GEO-EF1",
        description: "Livro de Geografia - Ensino Fundamental I",
        quantity: 280,
        unitPrice: 111.56,
        totalPrice: 31236.0,
      },
    ],
  },
  {
    orderId: "ord-030",
    orderNumber: "PED-2024-030",
    clientCode: "CNX005",
    clientName: "Colégio Saber",
    orderDate: "2024-11-20",
    deliveryDate: "2025-01-10",
    status: "pending",
    totalAmount: 89340.0,
    paymentStatus: "Pendente",
    items: [
      {
        productCode: "MAT-EM-2025",
        description: "Material Didático - Ensino Médio - 2025",
        quantity: 400,
        unitPrice: 167.9,
        totalPrice: 67160.0,
      },
      {
        productCode: "LIV-LIT-EM",
        description: "Livro de Literatura - Ensino Médio",
        quantity: 180,
        unitPrice: 123.22,
        totalPrice: 22180.0,
      },
    ],
  },
];

// Mock Invoices
export const mockInvoices: Invoice[] = [
  {
    invoiceId: "inv-001",
    invoiceNumber: "NF-2024-0001",
    orderId: "ord-001",
    issueDate: "2024-02-20",
    dueDate: "2024-03-20",
    amount: 45680.5,
    status: "paid",
    pdfUrl: "/invoices/nf-2024-0001.pdf",
    xmlUrl: "/invoices/nf-2024-0001.xml",
  },
  {
    invoiceId: "inv-002",
    invoiceNumber: "NF-2024-0002",
    orderId: "ord-002",
    issueDate: "2024-03-15",
    dueDate: "2024-04-15",
    amount: 67890.0,
    status: "pending",
    pdfUrl: "/invoices/nf-2024-0002.pdf",
    xmlUrl: "/invoices/nf-2024-0002.xml",
  },
  {
    invoiceId: "inv-003",
    invoiceNumber: "NF-2024-0003",
    orderId: "ord-003",
    issueDate: "2024-04-10",
    dueDate: "2024-05-10",
    amount: 89234.8,
    status: "overdue",
    pdfUrl: "/invoices/nf-2024-0003.pdf",
    xmlUrl: "/invoices/nf-2024-0003.xml",
  },
  {
    invoiceId: "inv-004",
    invoiceNumber: "NF-2024-0004",
    orderId: "ord-004",
    issueDate: "2024-05-01",
    dueDate: "2024-06-01",
    amount: 34567.9,
    status: "pending",
    pdfUrl: "/invoices/nf-2024-0004.pdf",
    xmlUrl: "/invoices/nf-2024-0004.xml",
  },
  {
    invoiceId: "inv-005",
    invoiceNumber: "NF-2024-0005",
    orderId: "ord-005",
    issueDate: "2024-04-12",
    dueDate: "2024-05-12",
    amount: 23450.0,
    status: "cancelled",
    pdfUrl: "/invoices/nf-2024-0005.pdf",
    xmlUrl: "/invoices/nf-2024-0005.xml",
  },
  {
    invoiceId: "inv-006",
    invoiceNumber: "NF-2024-0006",
    orderId: "ord-006",
    issueDate: "2024-06-15",
    dueDate: "2024-07-15",
    amount: 52890.4,
    status: "paid",
    pdfUrl: "/invoices/nf-2024-0006.pdf",
    xmlUrl: "/invoices/nf-2024-0006.xml",
  },
  {
    invoiceId: "inv-007",
    invoiceNumber: "NF-2024-0007",
    orderId: "ord-007",
    issueDate: "2024-07-30",
    dueDate: "2024-08-30",
    amount: 78901.2,
    status: "overdue",
    pdfUrl: "/invoices/nf-2024-0007.pdf",
    xmlUrl: "/invoices/nf-2024-0007.xml",
  },
  {
    invoiceId: "inv-008",
    invoiceNumber: "NF-2024-0008",
    orderId: "ord-008",
    issueDate: "2024-09-10",
    dueDate: "2024-10-10",
    amount: 91234.5,
    status: "pending",
    pdfUrl: "/invoices/nf-2024-0008.pdf",
    xmlUrl: "/invoices/nf-2024-0008.xml",
  },
  {
    invoiceId: "inv-009",
    invoiceNumber: "NF-2024-0009",
    orderId: "ord-009",
    issueDate: "2024-10-15",
    dueDate: "2024-11-15",
    amount: 105678.9,
    status: "pending",
    pdfUrl: "/invoices/nf-2024-0009.pdf",
    xmlUrl: "/invoices/nf-2024-0009.xml",
  },
  {
    invoiceId: "inv-010",
    invoiceNumber: "NF-2024-0010",
    orderId: "ord-010",
    issueDate: "2024-11-05",
    dueDate: "2024-12-05",
    amount: 62345.7,
    status: "pending",
    pdfUrl: "/invoices/nf-2024-0010.pdf",
    xmlUrl: "/invoices/nf-2024-0010.xml",
  },
  {
    invoiceId: "inv-011",
    invoiceNumber: "NF-2024-0011",
    orderId: "ord-001",
    issueDate: "2024-03-01",
    dueDate: "2024-04-01",
    amount: 15226.83,
    status: "paid",
    pdfUrl: "/invoices/nf-2024-0011.pdf",
    xmlUrl: "/invoices/nf-2024-0011.xml",
  },
  {
    invoiceId: "inv-012",
    invoiceNumber: "NF-2024-0012",
    orderId: "ord-004",
    issueDate: "2024-05-15",
    dueDate: "2024-06-15",
    amount: 11522.63,
    status: "paid",
    pdfUrl: "/invoices/nf-2024-0012.pdf",
    xmlUrl: "/invoices/nf-2024-0012.xml",
  },
  {
    invoiceId: "inv-013",
    invoiceNumber: "NF-2024-0013",
    orderId: "ord-006",
    issueDate: "2024-07-01",
    dueDate: "2024-08-01",
    amount: 17630.13,
    status: "paid",
    pdfUrl: "/invoices/nf-2024-0013.pdf",
    xmlUrl: "/invoices/nf-2024-0013.xml",
  },
  {
    invoiceId: "inv-014",
    invoiceNumber: "NF-2024-0014",
    orderId: "ord-002",
    issueDate: "2024-04-01",
    dueDate: "2024-05-01",
    amount: 22630.0,
    status: "overdue",
    pdfUrl: "/invoices/nf-2024-0014.pdf",
    xmlUrl: "/invoices/nf-2024-0014.xml",
  },
  {
    invoiceId: "inv-015",
    invoiceNumber: "NF-2024-0015",
    orderId: "ord-010",
    issueDate: "2024-11-20",
    dueDate: "2024-12-20",
    amount: 20781.9,
    status: "pending",
    pdfUrl: "/invoices/nf-2024-0015.pdf",
    xmlUrl: "/invoices/nf-2024-0015.xml",
  },
  // New invoices for expanded orders (CNX004-CNX008)
  {
    invoiceId: "inv-016",
    invoiceNumber: "NF-2024-0016",
    orderId: "ord-011",
    issueDate: "2024-02-28",
    dueDate: "2024-03-28",
    amount: 38900.0,
    status: "paid",
    pdfUrl: "/invoices/nf-2024-0016.pdf",
    xmlUrl: "/invoices/nf-2024-0016.xml",
  },
  {
    invoiceId: "inv-017",
    invoiceNumber: "NF-2024-0017",
    orderId: "ord-012",
    issueDate: "2024-06-05",
    dueDate: "2024-07-05",
    amount: 72340.5,
    status: "pending",
    pdfUrl: "/invoices/nf-2024-0017.pdf",
    xmlUrl: "/invoices/nf-2024-0017.xml",
  },
  {
    invoiceId: "inv-018",
    invoiceNumber: "NF-2024-0018",
    orderId: "ord-013",
    issueDate: "2024-09-20",
    dueDate: "2024-10-20",
    amount: 54890.0,
    status: "pending",
    pdfUrl: "/invoices/nf-2024-0018.pdf",
    xmlUrl: "/invoices/nf-2024-0018.xml",
  },
  {
    invoiceId: "inv-019",
    invoiceNumber: "NF-2024-0019",
    orderId: "ord-014",
    issueDate: "2024-03-20",
    dueDate: "2024-04-20",
    amount: 96780.0,
    status: "paid",
    pdfUrl: "/invoices/nf-2024-0019.pdf",
    xmlUrl: "/invoices/nf-2024-0019.xml",
  },
  {
    invoiceId: "inv-020",
    invoiceNumber: "NF-2024-0020",
    orderId: "ord-015",
    issueDate: "2024-07-01",
    dueDate: "2024-08-01",
    amount: 85670.0,
    status: "pending",
    pdfUrl: "/invoices/nf-2024-0020.pdf",
    xmlUrl: "/invoices/nf-2024-0020.xml",
  },
  {
    invoiceId: "inv-021",
    invoiceNumber: "NF-2024-0021",
    orderId: "ord-016",
    issueDate: "2024-10-30",
    dueDate: "2024-11-30",
    amount: 123450.0,
    status: "pending",
    pdfUrl: "/invoices/nf-2024-0021.pdf",
    xmlUrl: "/invoices/nf-2024-0021.xml",
  },
  {
    invoiceId: "inv-022",
    invoiceNumber: "NF-2024-0022",
    orderId: "ord-017",
    issueDate: "2024-05-10",
    dueDate: "2024-06-10",
    amount: 67890.0,
    status: "paid",
    pdfUrl: "/invoices/nf-2024-0022.pdf",
    xmlUrl: "/invoices/nf-2024-0022.xml",
  },
  {
    invoiceId: "inv-023",
    invoiceNumber: "NF-2024-0023",
    orderId: "ord-018",
    issueDate: "2024-08-15",
    dueDate: "2024-09-15",
    amount: 98760.0,
    status: "pending",
    pdfUrl: "/invoices/nf-2024-0023.pdf",
    xmlUrl: "/invoices/nf-2024-0023.xml",
  },
  {
    invoiceId: "inv-024",
    invoiceNumber: "NF-2024-0024",
    orderId: "ord-019",
    issueDate: "2024-11-25",
    dueDate: "2024-12-25",
    amount: 145670.0,
    status: "pending",
    pdfUrl: "/invoices/nf-2024-0024.pdf",
    xmlUrl: "/invoices/nf-2024-0024.xml",
  },
  {
    invoiceId: "inv-025",
    invoiceNumber: "NF-2024-0025",
    orderId: "ord-020",
    issueDate: "2024-04-15",
    dueDate: "2024-05-15",
    amount: 43560.0,
    status: "paid",
    pdfUrl: "/invoices/nf-2024-0025.pdf",
    xmlUrl: "/invoices/nf-2024-0025.xml",
  },
  {
    invoiceId: "inv-026",
    invoiceNumber: "NF-2024-0026",
    orderId: "ord-022",
    issueDate: "2024-11-10",
    dueDate: "2024-12-10",
    amount: 78900.0,
    status: "pending",
    pdfUrl: "/invoices/nf-2024-0026.pdf",
    xmlUrl: "/invoices/nf-2024-0026.xml",
  },
  {
    invoiceId: "inv-027",
    invoiceNumber: "NF-2024-0027",
    orderId: "ord-023",
    issueDate: "2024-03-10",
    dueDate: "2024-04-10",
    amount: 52340.0,
    status: "paid",
    pdfUrl: "/invoices/nf-2024-0027.pdf",
    xmlUrl: "/invoices/nf-2024-0027.xml",
  },
  {
    invoiceId: "inv-028",
    invoiceNumber: "NF-2024-0028",
    orderId: "ord-024",
    issueDate: "2024-06-25",
    dueDate: "2024-07-25",
    amount: 89670.0,
    status: "pending",
    pdfUrl: "/invoices/nf-2024-0028.pdf",
    xmlUrl: "/invoices/nf-2024-0028.xml",
  },
  {
    invoiceId: "inv-029",
    invoiceNumber: "NF-2024-0029",
    orderId: "ord-025",
    issueDate: "2024-10-05",
    dueDate: "2024-11-05",
    amount: 134560.0,
    status: "pending",
    pdfUrl: "/invoices/nf-2024-0029.pdf",
    xmlUrl: "/invoices/nf-2024-0029.xml",
  },
  {
    invoiceId: "inv-030",
    invoiceNumber: "NF-2024-0030",
    orderId: "ord-026",
    issueDate: "2024-12-01",
    dueDate: "2025-01-01",
    amount: 76890.0,
    status: "pending",
    pdfUrl: "/invoices/nf-2024-0030.pdf",
    xmlUrl: "/invoices/nf-2024-0030.xml",
  },
  {
    invoiceId: "inv-031",
    invoiceNumber: "NF-2024-0031",
    orderId: "ord-027",
    issueDate: "2024-12-20",
    dueDate: "2025-01-20",
    amount: 112340.0,
    status: "pending",
    pdfUrl: "/invoices/nf-2024-0031.pdf",
    xmlUrl: "/invoices/nf-2024-0031.xml",
  },
  {
    invoiceId: "inv-032",
    invoiceNumber: "NF-2024-0032",
    orderId: "ord-028",
    issueDate: "2025-01-15",
    dueDate: "2025-02-15",
    amount: 98760.0,
    status: "pending",
    pdfUrl: "/invoices/nf-2024-0032.pdf",
    xmlUrl: "/invoices/nf-2024-0032.xml",
  },
  {
    invoiceId: "inv-033",
    invoiceNumber: "NF-2024-0033",
    orderId: "ord-029",
    issueDate: "2025-01-05",
    dueDate: "2025-02-05",
    amount: 67450.0,
    status: "pending",
    pdfUrl: "/invoices/nf-2024-0033.pdf",
    xmlUrl: "/invoices/nf-2024-0033.xml",
  },
  {
    invoiceId: "inv-034",
    invoiceNumber: "NF-2024-0034",
    orderId: "ord-030",
    issueDate: "2025-01-10",
    dueDate: "2025-02-10",
    amount: 89340.0,
    status: "pending",
    pdfUrl: "/invoices/nf-2024-0034.pdf",
    xmlUrl: "/invoices/nf-2024-0034.xml",
  },
  // Additional invoices for split payments
  {
    invoiceId: "inv-035",
    invoiceNumber: "NF-2024-0035",
    orderId: "ord-011",
    issueDate: "2024-03-15",
    dueDate: "2024-04-15",
    amount: 12966.67,
    status: "paid",
    pdfUrl: "/invoices/nf-2024-0035.pdf",
    xmlUrl: "/invoices/nf-2024-0035.xml",
  },
  {
    invoiceId: "inv-036",
    invoiceNumber: "NF-2024-0036",
    orderId: "ord-012",
    issueDate: "2024-06-20",
    dueDate: "2024-07-20",
    amount: 24113.5,
    status: "pending",
    pdfUrl: "/invoices/nf-2024-0036.pdf",
    xmlUrl: "/invoices/nf-2024-0036.xml",
  },
  {
    invoiceId: "inv-037",
    invoiceNumber: "NF-2024-0037",
    orderId: "ord-013",
    issueDate: "2024-10-05",
    dueDate: "2024-11-05",
    amount: 18296.67,
    status: "pending",
    pdfUrl: "/invoices/nf-2024-0037.pdf",
    xmlUrl: "/invoices/nf-2024-0037.xml",
  },
  {
    invoiceId: "inv-038",
    invoiceNumber: "NF-2024-0038",
    orderId: "ord-014",
    issueDate: "2024-04-05",
    dueDate: "2024-05-05",
    amount: 32260.0,
    status: "paid",
    pdfUrl: "/invoices/nf-2024-0038.pdf",
    xmlUrl: "/invoices/nf-2024-0038.xml",
  },
  {
    invoiceId: "inv-039",
    invoiceNumber: "NF-2024-0039",
    orderId: "ord-015",
    issueDate: "2024-07-15",
    dueDate: "2024-08-15",
    amount: 28556.67,
    status: "paid",
    pdfUrl: "/invoices/nf-2024-0039.pdf",
    xmlUrl: "/invoices/nf-2024-0039.xml",
  },
  {
    invoiceId: "inv-040",
    invoiceNumber: "NF-2024-0040",
    orderId: "ord-016",
    issueDate: "2024-11-15",
    dueDate: "2024-12-15",
    amount: 41150.0,
    status: "pending",
    pdfUrl: "/invoices/nf-2024-0040.pdf",
    xmlUrl: "/invoices/nf-2024-0040.xml",
  },
  {
    invoiceId: "inv-041",
    invoiceNumber: "NF-2024-0041",
    orderId: "ord-017",
    issueDate: "2024-05-25",
    dueDate: "2024-06-25",
    amount: 22630.0,
    status: "paid",
    pdfUrl: "/invoices/nf-2024-0041.pdf",
    xmlUrl: "/invoices/nf-2024-0041.xml",
  },
  {
    invoiceId: "inv-042",
    invoiceNumber: "NF-2024-0042",
    orderId: "ord-018",
    issueDate: "2024-09-01",
    dueDate: "2024-10-01",
    amount: 32920.0,
    status: "pending",
    pdfUrl: "/invoices/nf-2024-0042.pdf",
    xmlUrl: "/invoices/nf-2024-0042.xml",
  },
  {
    invoiceId: "inv-043",
    invoiceNumber: "NF-2024-0043",
    orderId: "ord-019",
    issueDate: "2024-12-10",
    dueDate: "2025-01-10",
    amount: 48556.67,
    status: "pending",
    pdfUrl: "/invoices/nf-2024-0043.pdf",
    xmlUrl: "/invoices/nf-2024-0043.xml",
  },
  {
    invoiceId: "inv-044",
    invoiceNumber: "NF-2024-0044",
    orderId: "ord-020",
    issueDate: "2024-05-01",
    dueDate: "2024-06-01",
    amount: 14520.0,
    status: "paid",
    pdfUrl: "/invoices/nf-2024-0044.pdf",
    xmlUrl: "/invoices/nf-2024-0044.xml",
  },
  {
    invoiceId: "inv-045",
    invoiceNumber: "NF-2024-0045",
    orderId: "ord-022",
    issueDate: "2024-11-25",
    dueDate: "2024-12-25",
    amount: 26300.0,
    status: "pending",
    pdfUrl: "/invoices/nf-2024-0045.pdf",
    xmlUrl: "/invoices/nf-2024-0045.xml",
  },
  {
    invoiceId: "inv-046",
    invoiceNumber: "NF-2024-0046",
    orderId: "ord-023",
    issueDate: "2024-03-25",
    dueDate: "2024-04-25",
    amount: 17446.67,
    status: "paid",
    pdfUrl: "/invoices/nf-2024-0046.pdf",
    xmlUrl: "/invoices/nf-2024-0046.xml",
  },
  {
    invoiceId: "inv-047",
    invoiceNumber: "NF-2024-0047",
    orderId: "ord-024",
    issueDate: "2024-07-10",
    dueDate: "2024-08-10",
    amount: 29890.0,
    status: "paid",
    pdfUrl: "/invoices/nf-2024-0047.pdf",
    xmlUrl: "/invoices/nf-2024-0047.xml",
  },
  {
    invoiceId: "inv-048",
    invoiceNumber: "NF-2024-0048",
    orderId: "ord-025",
    issueDate: "2024-10-20",
    dueDate: "2024-11-20",
    amount: 44853.33,
    status: "pending",
    pdfUrl: "/invoices/nf-2024-0048.pdf",
    xmlUrl: "/invoices/nf-2024-0048.xml",
  },
  {
    invoiceId: "inv-049",
    invoiceNumber: "NF-2024-0049",
    orderId: "ord-026",
    issueDate: "2024-12-15",
    dueDate: "2025-01-15",
    amount: 25630.0,
    status: "pending",
    pdfUrl: "/invoices/nf-2024-0049.pdf",
    xmlUrl: "/invoices/nf-2024-0049.xml",
  },
  {
    invoiceId: "inv-050",
    invoiceNumber: "NF-2024-0050",
    orderId: "ord-027",
    issueDate: "2025-01-05",
    dueDate: "2025-02-05",
    amount: 37446.67,
    status: "pending",
    pdfUrl: "/invoices/nf-2024-0050.pdf",
    xmlUrl: "/invoices/nf-2024-0050.xml",
  },
];

// Mock Installments
export const mockInstallments: Installment[] = [
  // Order 001 - 3 installments (all paid)
  {
    installmentId: "inst-001",
    orderId: "ord-001",
    invoiceId: "inv-001",
    installmentNumber: 1,
    dueDate: "2024-03-20",
    amount: 15226.83,
    status: "paid",
    paymentDate: "2024-03-18",
    boletoUrl: "/boletos/bol-001-1.pdf",
    boletoBarcode: "23793.38128 60000.123456 78901.234567 8 12340000015226",
    paymentProofUrl: "/proofs/proof-001-1.pdf",
  },
  {
    installmentId: "inst-002",
    orderId: "ord-001",
    invoiceId: "inv-001",
    installmentNumber: 2,
    dueDate: "2024-04-20",
    amount: 15226.83,
    status: "paid",
    paymentDate: "2024-04-19",
    boletoUrl: "/boletos/bol-001-2.pdf",
    boletoBarcode: "23793.38128 60000.123456 78901.234568 9 12340000015226",
    paymentProofUrl: "/proofs/proof-001-2.pdf",
  },
  {
    installmentId: "inst-003",
    orderId: "ord-001",
    invoiceId: "inv-001",
    installmentNumber: 3,
    dueDate: "2024-05-20",
    amount: 15226.84,
    status: "paid",
    paymentDate: "2024-05-17",
    boletoUrl: "/boletos/bol-001-3.pdf",
    boletoBarcode: "23793.38128 60000.123456 78901.234569 1 12340000015226",
    paymentProofUrl: "/proofs/proof-001-3.pdf",
  },
  // Order 002 - 3 installments (1 paid, 1 overdue, 1 pending)
  {
    installmentId: "inst-004",
    orderId: "ord-002",
    invoiceId: "inv-002",
    installmentNumber: 1,
    dueDate: "2024-04-15",
    amount: 22630.0,
    status: "paid",
    paymentDate: "2024-04-14",
    boletoUrl: "/boletos/bol-002-1.pdf",
    boletoBarcode: "23793.38128 60000.234567 78901.345678 2 12340000022630",
    paymentProofUrl: "/proofs/proof-002-1.pdf",
  },
  {
    installmentId: "inst-005",
    orderId: "ord-002",
    invoiceId: "inv-002",
    installmentNumber: 2,
    dueDate: "2024-05-15",
    amount: 22630.0,
    status: "overdue",
    boletoUrl: "/boletos/bol-002-2.pdf",
    boletoBarcode: "23793.38128 60000.234567 78901.345679 3 12340000022630",
  },
  {
    installmentId: "inst-006",
    orderId: "ord-002",
    invoiceId: "inv-002",
    installmentNumber: 3,
    dueDate: "2024-06-15",
    amount: 22630.0,
    status: "pending",
    boletoUrl: "/boletos/bol-002-3.pdf",
    boletoBarcode: "23793.38128 60000.234567 78901.345680 4 12340000022630",
  },
  // Order 003 - 3 installments (all overdue)
  {
    installmentId: "inst-007",
    orderId: "ord-003",
    invoiceId: "inv-003",
    installmentNumber: 1,
    dueDate: "2024-05-10",
    amount: 29744.93,
    status: "overdue",
    boletoUrl: "/boletos/bol-003-1.pdf",
    boletoBarcode: "23793.38128 60000.345678 78901.456789 5 12340000029744",
  },
  {
    installmentId: "inst-008",
    orderId: "ord-003",
    invoiceId: "inv-003",
    installmentNumber: 2,
    dueDate: "2024-06-10",
    amount: 29744.93,
    status: "overdue",
    boletoUrl: "/boletos/bol-003-2.pdf",
    boletoBarcode: "23793.38128 60000.345678 78901.456790 6 12340000029744",
  },
  {
    installmentId: "inst-009",
    orderId: "ord-003",
    invoiceId: "inv-003",
    installmentNumber: 3,
    dueDate: "2024-07-10",
    amount: 29744.94,
    status: "overdue",
    boletoUrl: "/boletos/bol-003-3.pdf",
    boletoBarcode: "23793.38128 60000.345678 78901.456791 7 12340000029744",
  },
  // Order 004 - 3 installments (1 paid, 2 pending)
  {
    installmentId: "inst-010",
    orderId: "ord-004",
    invoiceId: "inv-004",
    installmentNumber: 1,
    dueDate: "2024-06-01",
    amount: 11522.63,
    status: "paid",
    paymentDate: "2024-05-30",
    boletoUrl: "/boletos/bol-004-1.pdf",
    boletoBarcode: "23793.38128 60000.456789 78901.567890 8 12340000011522",
    paymentProofUrl: "/proofs/proof-004-1.pdf",
  },
  {
    installmentId: "inst-011",
    orderId: "ord-004",
    invoiceId: "inv-004",
    installmentNumber: 2,
    dueDate: "2024-07-01",
    amount: 11522.63,
    status: "pending",
    boletoUrl: "/boletos/bol-004-2.pdf",
    boletoBarcode: "23793.38128 60000.456789 78901.567891 9 12340000011522",
  },
  {
    installmentId: "inst-012",
    orderId: "ord-004",
    invoiceId: "inv-004",
    installmentNumber: 3,
    dueDate: "2024-08-01",
    amount: 11522.64,
    status: "pending",
    boletoUrl: "/boletos/bol-004-3.pdf",
    boletoBarcode: "23793.38128 60000.456789 78901.567892 1 12340000011522",
  },
  // Order 006 - 3 installments (all paid)
  {
    installmentId: "inst-013",
    orderId: "ord-006",
    invoiceId: "inv-006",
    installmentNumber: 1,
    dueDate: "2024-07-15",
    amount: 17630.13,
    status: "paid",
    paymentDate: "2024-07-14",
    boletoUrl: "/boletos/bol-006-1.pdf",
    boletoBarcode: "23793.38128 60000.567890 78901.678901 2 12340000017630",
    paymentProofUrl: "/proofs/proof-006-1.pdf",
  },
  {
    installmentId: "inst-014",
    orderId: "ord-006",
    invoiceId: "inv-006",
    installmentNumber: 2,
    dueDate: "2024-08-15",
    amount: 17630.13,
    status: "paid",
    paymentDate: "2024-08-13",
    boletoUrl: "/boletos/bol-006-2.pdf",
    boletoBarcode: "23793.38128 60000.567890 78901.678902 3 12340000017630",
    paymentProofUrl: "/proofs/proof-006-2.pdf",
  },
  {
    installmentId: "inst-015",
    orderId: "ord-006",
    invoiceId: "inv-006",
    installmentNumber: 3,
    dueDate: "2024-09-15",
    amount: 17630.14,
    status: "paid",
    paymentDate: "2024-09-14",
    boletoUrl: "/boletos/bol-006-3.pdf",
    boletoBarcode: "23793.38128 60000.567890 78901.678903 4 12340000017630",
    paymentProofUrl: "/proofs/proof-006-3.pdf",
  },
  // Order 007 - 3 installments (all overdue)
  {
    installmentId: "inst-016",
    orderId: "ord-007",
    invoiceId: "inv-007",
    installmentNumber: 1,
    dueDate: "2024-08-30",
    amount: 26300.4,
    status: "overdue",
    boletoUrl: "/boletos/bol-007-1.pdf",
    boletoBarcode: "23793.38128 60000.678901 78901.789012 5 12340000026300",
  },
  {
    installmentId: "inst-017",
    orderId: "ord-007",
    invoiceId: "inv-007",
    installmentNumber: 2,
    dueDate: "2024-09-30",
    amount: 26300.4,
    status: "overdue",
    boletoUrl: "/boletos/bol-007-2.pdf",
    boletoBarcode: "23793.38128 60000.678901 78901.789013 6 12340000026300",
  },
  {
    installmentId: "inst-018",
    orderId: "ord-007",
    invoiceId: "inv-007",
    installmentNumber: 3,
    dueDate: "2024-10-30",
    amount: 26300.4,
    status: "overdue",
    boletoUrl: "/boletos/bol-007-3.pdf",
    boletoBarcode: "23793.38128 60000.678901 78901.789014 7 12340000026300",
  },
  // Order 008 - 3 installments (all pending)
  {
    installmentId: "inst-019",
    orderId: "ord-008",
    invoiceId: "inv-008",
    installmentNumber: 1,
    dueDate: "2024-10-10",
    amount: 30411.5,
    status: "pending",
    boletoUrl: "/boletos/bol-008-1.pdf",
    boletoBarcode: "23793.38128 60000.789012 78901.890123 8 12340000030411",
  },
  {
    installmentId: "inst-020",
    orderId: "ord-008",
    invoiceId: "inv-008",
    installmentNumber: 2,
    dueDate: "2024-11-10",
    amount: 30411.5,
    status: "pending",
    boletoUrl: "/boletos/bol-008-2.pdf",
    boletoBarcode: "23793.38128 60000.789012 78901.890124 9 12340000030411",
  },
  {
    installmentId: "inst-021",
    orderId: "ord-008",
    invoiceId: "inv-008",
    installmentNumber: 3,
    dueDate: "2024-12-10",
    amount: 30411.5,
    status: "pending",
    boletoUrl: "/boletos/bol-008-3.pdf",
    boletoBarcode: "23793.38128 60000.789012 78901.890125 1 12340000030411",
  },
  // Order 009 - 3 installments (all pending)
  {
    installmentId: "inst-022",
    orderId: "ord-009",
    invoiceId: "inv-009",
    installmentNumber: 1,
    dueDate: "2024-11-15",
    amount: 35226.3,
    status: "pending",
    boletoUrl: "/boletos/bol-009-1.pdf",
    boletoBarcode: "23793.38128 60000.890123 78901.901234 2 12340000035226",
  },
  {
    installmentId: "inst-023",
    orderId: "ord-009",
    invoiceId: "inv-009",
    installmentNumber: 2,
    dueDate: "2024-12-15",
    amount: 35226.3,
    status: "pending",
    boletoUrl: "/boletos/bol-009-2.pdf",
    boletoBarcode: "23793.38128 60000.890123 78901.901235 3 12340000035226",
  },
  {
    installmentId: "inst-024",
    orderId: "ord-009",
    invoiceId: "inv-009",
    installmentNumber: 3,
    dueDate: "2025-01-15",
    amount: 35226.3,
    status: "pending",
    boletoUrl: "/boletos/bol-009-3.pdf",
    boletoBarcode: "23793.38128 60000.890123 78901.901236 4 12340000035226",
  },
  // Order 010 - 3 installments (all pending)
  {
    installmentId: "inst-025",
    orderId: "ord-010",
    invoiceId: "inv-010",
    installmentNumber: 1,
    dueDate: "2024-12-05",
    amount: 20781.9,
    status: "pending",
    boletoUrl: "/boletos/bol-010-1.pdf",
    boletoBarcode: "23793.38128 60000.901234 78901.012345 5 12340000020781",
  },
  {
    installmentId: "inst-026",
    orderId: "ord-010",
    invoiceId: "inv-010",
    installmentNumber: 2,
    dueDate: "2025-01-05",
    amount: 20781.9,
    status: "pending",
    boletoUrl: "/boletos/bol-010-2.pdf",
    boletoBarcode: "23793.38128 60000.901234 78901.012346 6 12340000020781",
  },
  {
    installmentId: "inst-027",
    orderId: "ord-010",
    invoiceId: "inv-010",
    installmentNumber: 3,
    dueDate: "2025-02-05",
    amount: 20781.9,
    status: "pending",
    boletoUrl: "/boletos/bol-010-3.pdf",
    boletoBarcode: "23793.38128 60000.901234 78901.012347 7 12340000020781",
  },
  // Additional installments for invoice 011
  {
    installmentId: "inst-028",
    orderId: "ord-001",
    invoiceId: "inv-011",
    installmentNumber: 1,
    dueDate: "2024-04-01",
    amount: 5075.61,
    status: "paid",
    paymentDate: "2024-03-30",
    boletoUrl: "/boletos/bol-011-1.pdf",
    boletoBarcode: "23793.38128 60000.012345 78901.123456 8 12340000005075",
    paymentProofUrl: "/proofs/proof-011-1.pdf",
  },
  {
    installmentId: "inst-029",
    orderId: "ord-001",
    invoiceId: "inv-011",
    installmentNumber: 2,
    dueDate: "2024-05-01",
    amount: 5075.61,
    status: "paid",
    paymentDate: "2024-04-28",
    boletoUrl: "/boletos/bol-011-2.pdf",
    boletoBarcode: "23793.38128 60000.012345 78901.123457 9 12340000005075",
    paymentProofUrl: "/proofs/proof-011-2.pdf",
  },
  {
    installmentId: "inst-030",
    orderId: "ord-001",
    invoiceId: "inv-011",
    installmentNumber: 3,
    dueDate: "2024-06-01",
    amount: 5075.61,
    status: "paid",
    paymentDate: "2024-05-30",
    boletoUrl: "/boletos/bol-011-3.pdf",
    boletoBarcode: "23793.38128 60000.012345 78901.123458 1 12340000005075",
    paymentProofUrl: "/proofs/proof-011-3.pdf",
  },
  // Additional installments for invoice 012
  {
    installmentId: "inst-031",
    orderId: "ord-004",
    invoiceId: "inv-012",
    installmentNumber: 1,
    dueDate: "2024-06-15",
    amount: 3840.88,
    status: "paid",
    paymentDate: "2024-06-14",
    boletoUrl: "/boletos/bol-012-1.pdf",
    boletoBarcode: "23793.38128 60000.123567 78901.234678 2 12340000003840",
    paymentProofUrl: "/proofs/proof-012-1.pdf",
  },
  {
    installmentId: "inst-032",
    orderId: "ord-004",
    invoiceId: "inv-012",
    installmentNumber: 2,
    dueDate: "2024-07-15",
    amount: 3840.88,
    status: "paid",
    paymentDate: "2024-07-13",
    boletoUrl: "/boletos/bol-012-2.pdf",
    boletoBarcode: "23793.38128 60000.123567 78901.234679 3 12340000003840",
    paymentProofUrl: "/proofs/proof-012-2.pdf",
  },
  {
    installmentId: "inst-033",
    orderId: "ord-004",
    invoiceId: "inv-012",
    installmentNumber: 3,
    dueDate: "2024-08-15",
    amount: 3840.87,
    status: "paid",
    paymentDate: "2024-08-14",
    boletoUrl: "/boletos/bol-012-3.pdf",
    boletoBarcode: "23793.38128 60000.123567 78901.234680 4 12340000003840",
    paymentProofUrl: "/proofs/proof-012-3.pdf",
  },
  // Additional installments for invoice 013
  {
    installmentId: "inst-034",
    orderId: "ord-006",
    invoiceId: "inv-013",
    installmentNumber: 1,
    dueDate: "2024-08-01",
    amount: 5876.71,
    status: "paid",
    paymentDate: "2024-07-30",
    boletoUrl: "/boletos/bol-013-1.pdf",
    boletoBarcode: "23793.38128 60000.234678 78901.345789 5 12340000005876",
    paymentProofUrl: "/proofs/proof-013-1.pdf",
  },
  {
    installmentId: "inst-035",
    orderId: "ord-006",
    invoiceId: "inv-013",
    installmentNumber: 2,
    dueDate: "2024-09-01",
    amount: 5876.71,
    status: "paid",
    paymentDate: "2024-08-29",
    boletoUrl: "/boletos/bol-013-2.pdf",
    boletoBarcode: "23793.38128 60000.234678 78901.345790 6 12340000005876",
    paymentProofUrl: "/proofs/proof-013-2.pdf",
  },
  {
    installmentId: "inst-036",
    orderId: "ord-006",
    invoiceId: "inv-013",
    installmentNumber: 3,
    dueDate: "2024-10-01",
    amount: 5876.71,
    status: "paid",
    paymentDate: "2024-09-28",
    boletoUrl: "/boletos/bol-013-3.pdf",
    boletoBarcode: "23793.38128 60000.234678 78901.345791 7 12340000005876",
    paymentProofUrl: "/proofs/proof-013-3.pdf",
  },
  // Additional installments for invoice 014
  {
    installmentId: "inst-037",
    orderId: "ord-002",
    invoiceId: "inv-014",
    installmentNumber: 1,
    dueDate: "2024-05-01",
    amount: 7543.33,
    status: "overdue",
    boletoUrl: "/boletos/bol-014-1.pdf",
    boletoBarcode: "23793.38128 60000.345789 78901.456890 8 12340000007543",
  },
  {
    installmentId: "inst-038",
    orderId: "ord-002",
    invoiceId: "inv-014",
    installmentNumber: 2,
    dueDate: "2024-06-01",
    amount: 7543.33,
    status: "overdue",
    boletoUrl: "/boletos/bol-014-2.pdf",
    boletoBarcode: "23793.38128 60000.345789 78901.456891 9 12340000007543",
  },
  {
    installmentId: "inst-039",
    orderId: "ord-002",
    invoiceId: "inv-014",
    installmentNumber: 3,
    dueDate: "2024-07-01",
    amount: 7543.34,
    status: "overdue",
    boletoUrl: "/boletos/bol-014-3.pdf",
    boletoBarcode: "23793.38128 60000.345789 78901.456892 1 12340000007543",
  },
  // Additional installments for invoice 015
  {
    installmentId: "inst-040",
    orderId: "ord-010",
    invoiceId: "inv-015",
    installmentNumber: 1,
    dueDate: "2024-12-20",
    amount: 6927.3,
    status: "pending",
    boletoUrl: "/boletos/bol-015-1.pdf",
    boletoBarcode: "23793.38128 60000.456890 78901.567901 2 12340000006927",
  },
  {
    installmentId: "inst-041",
    orderId: "ord-010",
    invoiceId: "inv-015",
    installmentNumber: 2,
    dueDate: "2025-01-20",
    amount: 6927.3,
    status: "pending",
    boletoUrl: "/boletos/bol-015-2.pdf",
    boletoBarcode: "23793.38128 60000.456890 78901.567902 3 12340000006927",
  },
  {
    installmentId: "inst-042",
    orderId: "ord-010",
    invoiceId: "inv-015",
    installmentNumber: 3,
    dueDate: "2025-02-20",
    amount: 6927.3,
    status: "pending",
    boletoUrl: "/boletos/bol-015-3.pdf",
    boletoBarcode: "23793.38128 60000.456890 78901.567903 4 12340000006927",
  },
  // Extra installments to reach 45 total
  {
    installmentId: "inst-043",
    orderId: "ord-001",
    invoiceId: "inv-001",
    installmentNumber: 4,
    dueDate: "2024-06-20",
    amount: 5000.0,
    status: "paid",
    paymentDate: "2024-06-18",
    boletoUrl: "/boletos/bol-001-4.pdf",
    boletoBarcode: "23793.38128 60000.567901 78901.678012 5 12340000005000",
    paymentProofUrl: "/proofs/proof-001-4.pdf",
  },
  {
    installmentId: "inst-044",
    orderId: "ord-002",
    invoiceId: "inv-002",
    installmentNumber: 4,
    dueDate: "2024-07-15",
    amount: 5000.0,
    status: "pending",
    boletoUrl: "/boletos/bol-002-4.pdf",
    boletoBarcode: "23793.38128 60000.678012 78901.789123 6 12340000005000",
  },
  {
    installmentId: "inst-045",
    orderId: "ord-003",
    invoiceId: "inv-003",
    installmentNumber: 4,
    dueDate: "2024-08-10",
    amount: 5000.0,
    status: "overdue",
    boletoUrl: "/boletos/bol-003-4.pdf",
    boletoBarcode: "23793.38128 60000.789123 78901.890234 7 12340000005000",
  },
  // New installments for orders 011-030 (105 new installments)
  // Order 011 - 3 installments (all paid)
  {
    installmentId: "inst-046",
    orderId: "ord-011",
    invoiceId: "inv-016",
    installmentNumber: 1,
    dueDate: "2024-03-28",
    amount: 12966.67,
    status: "paid",
    paymentDate: "2024-03-27",
    boletoUrl: "/boletos/bol-016-1.pdf",
    boletoBarcode: "23793.38128 60000.890234 78901.901345 8 12340000012966",
    paymentProofUrl: "/proofs/proof-016-1.pdf",
  },
  {
    installmentId: "inst-047",
    orderId: "ord-011",
    invoiceId: "inv-016",
    installmentNumber: 2,
    dueDate: "2024-04-28",
    amount: 12966.67,
    status: "paid",
    paymentDate: "2024-04-26",
    boletoUrl: "/boletos/bol-016-2.pdf",
    boletoBarcode: "23793.38128 60000.890234 78901.901346 9 12340000012966",
    paymentProofUrl: "/proofs/proof-016-2.pdf",
  },
  {
    installmentId: "inst-048",
    orderId: "ord-011",
    invoiceId: "inv-016",
    installmentNumber: 3,
    dueDate: "2024-05-28",
    amount: 12966.66,
    status: "paid",
    paymentDate: "2024-05-27",
    boletoUrl: "/boletos/bol-016-3.pdf",
    boletoBarcode: "23793.38128 60000.890234 78901.901347 1 12340000012966",
    paymentProofUrl: "/proofs/proof-016-3.pdf",
  },
  // Order 012-030 installments (3 per order)
  {
    installmentId: "inst-049",
    orderId: "ord-012",
    invoiceId: "inv-017",
    installmentNumber: 1,
    dueDate: "2024-07-05",
    amount: 24113.5,
    status: "pending",
    boletoUrl: "/boletos/bol-017-1.pdf",
    boletoBarcode: "23793.38128 60000.901345 78901.012456 2 12340000024113",
  },
  {
    installmentId: "inst-050",
    orderId: "ord-012",
    invoiceId: "inv-017",
    installmentNumber: 2,
    dueDate: "2024-08-05",
    amount: 24113.5,
    status: "pending",
    boletoUrl: "/boletos/bol-017-2.pdf",
    boletoBarcode: "23793.38128 60000.901345 78901.012457 3 12340000024113",
  },
  {
    installmentId: "inst-051",
    orderId: "ord-012",
    invoiceId: "inv-017",
    installmentNumber: 3,
    dueDate: "2024-09-05",
    amount: 24113.5,
    status: "pending",
    boletoUrl: "/boletos/bol-017-3.pdf",
    boletoBarcode: "23793.38128 60000.901345 78901.012458 4 12340000024113",
  },
  {
    installmentId: "inst-052",
    orderId: "ord-013",
    invoiceId: "inv-018",
    installmentNumber: 1,
    dueDate: "2024-10-20",
    amount: 18296.67,
    status: "pending",
    boletoUrl: "/boletos/bol-018-1.pdf",
    boletoBarcode: "23793.38128 60000.012456 78901.123567 5 12340000018296",
  },
  {
    installmentId: "inst-053",
    orderId: "ord-013",
    invoiceId: "inv-018",
    installmentNumber: 2,
    dueDate: "2024-11-20",
    amount: 18296.67,
    status: "pending",
    boletoUrl: "/boletos/bol-018-2.pdf",
    boletoBarcode: "23793.38128 60000.012456 78901.123568 6 12340000018296",
  },
  {
    installmentId: "inst-054",
    orderId: "ord-013",
    invoiceId: "inv-018",
    installmentNumber: 3,
    dueDate: "2024-12-20",
    amount: 18296.66,
    status: "pending",
    boletoUrl: "/boletos/bol-018-3.pdf",
    boletoBarcode: "23793.38128 60000.012456 78901.123569 7 12340000018296",
  },
  {
    installmentId: "inst-055",
    orderId: "ord-014",
    invoiceId: "inv-019",
    installmentNumber: 1,
    dueDate: "2024-04-20",
    amount: 32260.0,
    status: "paid",
    paymentDate: "2024-04-18",
    boletoUrl: "/boletos/bol-019-1.pdf",
    boletoBarcode: "23793.38128 60000.123567 78901.234678 8 12340000032260",
    paymentProofUrl: "/proofs/proof-019-1.pdf",
  },
  {
    installmentId: "inst-056",
    orderId: "ord-014",
    invoiceId: "inv-019",
    installmentNumber: 2,
    dueDate: "2024-05-20",
    amount: 32260.0,
    status: "paid",
    paymentDate: "2024-05-19",
    boletoUrl: "/boletos/bol-019-2.pdf",
    boletoBarcode: "23793.38128 60000.123567 78901.234679 9 12340000032260",
    paymentProofUrl: "/proofs/proof-019-2.pdf",
  },
  {
    installmentId: "inst-057",
    orderId: "ord-014",
    invoiceId: "inv-019",
    installmentNumber: 3,
    dueDate: "2024-06-20",
    amount: 32260.0,
    status: "paid",
    paymentDate: "2024-06-17",
    boletoUrl: "/boletos/bol-019-3.pdf",
    boletoBarcode: "23793.38128 60000.123567 78901.234680 1 12340000032260",
    paymentProofUrl: "/proofs/proof-019-3.pdf",
  },
  {
    installmentId: "inst-058",
    orderId: "ord-015",
    invoiceId: "inv-020",
    installmentNumber: 1,
    dueDate: "2024-08-01",
    amount: 28556.67,
    status: "paid",
    paymentDate: "2024-07-30",
    boletoUrl: "/boletos/bol-020-1.pdf",
    boletoBarcode: "23793.38128 60000.234678 78901.345789 2 12340000028556",
    paymentProofUrl: "/proofs/proof-020-1.pdf",
  },
  {
    installmentId: "inst-059",
    orderId: "ord-015",
    invoiceId: "inv-020",
    installmentNumber: 2,
    dueDate: "2024-09-01",
    amount: 28556.67,
    status: "pending",
    boletoUrl: "/boletos/bol-020-2.pdf",
    boletoBarcode: "23793.38128 60000.234678 78901.345790 3 12340000028556",
  },
  {
    installmentId: "inst-060",
    orderId: "ord-015",
    invoiceId: "inv-020",
    installmentNumber: 3,
    dueDate: "2024-10-01",
    amount: 28556.66,
    status: "pending",
    boletoUrl: "/boletos/bol-020-3.pdf",
    boletoBarcode: "23793.38128 60000.234678 78901.345791 4 12340000028556",
  },
  // Continue with remaining 90 installments for orders 016-030 (3 each)
  {
    installmentId: "inst-061",
    orderId: "ord-016",
    invoiceId: "inv-021",
    installmentNumber: 1,
    dueDate: "2024-11-30",
    amount: 41150.0,
    status: "pending",
    boletoUrl: "/boletos/bol-021-1.pdf",
    boletoBarcode: "23793.38128 60000.345789 78901.456890 5 12340000041150",
  },
  {
    installmentId: "inst-062",
    orderId: "ord-016",
    invoiceId: "inv-021",
    installmentNumber: 2,
    dueDate: "2024-12-30",
    amount: 41150.0,
    status: "pending",
    boletoUrl: "/boletos/bol-021-2.pdf",
    boletoBarcode: "23793.38128 60000.345789 78901.456891 6 12340000041150",
  },
  {
    installmentId: "inst-063",
    orderId: "ord-016",
    invoiceId: "inv-021",
    installmentNumber: 3,
    dueDate: "2025-01-30",
    amount: 41150.0,
    status: "pending",
    boletoUrl: "/boletos/bol-021-3.pdf",
    boletoBarcode: "23793.38128 60000.345789 78901.456892 7 12340000041150",
  },
  {
    installmentId: "inst-064",
    orderId: "ord-017",
    invoiceId: "inv-022",
    installmentNumber: 1,
    dueDate: "2024-06-10",
    amount: 22630.0,
    status: "paid",
    paymentDate: "2024-06-08",
    boletoUrl: "/boletos/bol-022-1.pdf",
    boletoBarcode: "23793.38128 60000.456890 78901.567901 8 12340000022630",
    paymentProofUrl: "/proofs/proof-022-1.pdf",
  },
  {
    installmentId: "inst-065",
    orderId: "ord-017",
    invoiceId: "inv-022",
    installmentNumber: 2,
    dueDate: "2024-07-10",
    amount: 22630.0,
    status: "paid",
    paymentDate: "2024-07-09",
    boletoUrl: "/boletos/bol-022-2.pdf",
    boletoBarcode: "23793.38128 60000.456890 78901.567902 9 12340000022630",
    paymentProofUrl: "/proofs/proof-022-2.pdf",
  },
  {
    installmentId: "inst-066",
    orderId: "ord-017",
    invoiceId: "inv-022",
    installmentNumber: 3,
    dueDate: "2024-08-10",
    amount: 22630.0,
    status: "paid",
    paymentDate: "2024-08-07",
    boletoUrl: "/boletos/bol-022-3.pdf",
    boletoBarcode: "23793.38128 60000.456890 78901.567903 1 12340000022630",
    paymentProofUrl: "/proofs/proof-022-3.pdf",
  },
  {
    installmentId: "inst-067",
    orderId: "ord-018",
    invoiceId: "inv-023",
    installmentNumber: 1,
    dueDate: "2024-09-15",
    amount: 32920.0,
    status: "pending",
    boletoUrl: "/boletos/bol-023-1.pdf",
    boletoBarcode: "23793.38128 60000.567901 78901.678012 2 12340000032920",
  },
  {
    installmentId: "inst-068",
    orderId: "ord-018",
    invoiceId: "inv-023",
    installmentNumber: 2,
    dueDate: "2024-10-15",
    amount: 32920.0,
    status: "pending",
    boletoUrl: "/boletos/bol-023-2.pdf",
    boletoBarcode: "23793.38128 60000.567901 78901.678013 3 12340000032920",
  },
  {
    installmentId: "inst-069",
    orderId: "ord-018",
    invoiceId: "inv-023",
    installmentNumber: 3,
    dueDate: "2024-11-15",
    amount: 32920.0,
    status: "pending",
    boletoUrl: "/boletos/bol-023-3.pdf",
    boletoBarcode: "23793.38128 60000.567901 78901.678014 4 12340000032920",
  },
  {
    installmentId: "inst-070",
    orderId: "ord-019",
    invoiceId: "inv-024",
    installmentNumber: 1,
    dueDate: "2024-12-25",
    amount: 48556.67,
    status: "pending",
    boletoUrl: "/boletos/bol-024-1.pdf",
    boletoBarcode: "23793.38128 60000.678012 78901.789123 5 12340000048556",
  },
  {
    installmentId: "inst-071",
    orderId: "ord-019",
    invoiceId: "inv-024",
    installmentNumber: 2,
    dueDate: "2025-01-25",
    amount: 48556.67,
    status: "pending",
    boletoUrl: "/boletos/bol-024-2.pdf",
    boletoBarcode: "23793.38128 60000.678012 78901.789124 6 12340000048556",
  },
  {
    installmentId: "inst-072",
    orderId: "ord-019",
    invoiceId: "inv-024",
    installmentNumber: 3,
    dueDate: "2025-02-25",
    amount: 48556.66,
    status: "pending",
    boletoUrl: "/boletos/bol-024-3.pdf",
    boletoBarcode: "23793.38128 60000.678012 78901.789125 7 12340000048556",
  },
  {
    installmentId: "inst-073",
    orderId: "ord-020",
    invoiceId: "inv-025",
    installmentNumber: 1,
    dueDate: "2024-05-15",
    amount: 14520.0,
    status: "paid",
    paymentDate: "2024-05-14",
    boletoUrl: "/boletos/bol-025-1.pdf",
    boletoBarcode: "23793.38128 60000.789123 78901.890234 8 12340000014520",
    paymentProofUrl: "/proofs/proof-025-1.pdf",
  },
  {
    installmentId: "inst-074",
    orderId: "ord-020",
    invoiceId: "inv-025",
    installmentNumber: 2,
    dueDate: "2024-06-15",
    amount: 14520.0,
    status: "paid",
    paymentDate: "2024-06-13",
    boletoUrl: "/boletos/bol-025-2.pdf",
    boletoBarcode: "23793.38128 60000.789123 78901.890235 9 12340000014520",
    paymentProofUrl: "/proofs/proof-025-2.pdf",
  },
  {
    installmentId: "inst-075",
    orderId: "ord-020",
    invoiceId: "inv-025",
    installmentNumber: 3,
    dueDate: "2024-07-15",
    amount: 14520.0,
    status: "paid",
    paymentDate: "2024-07-12",
    boletoUrl: "/boletos/bol-025-3.pdf",
    boletoBarcode: "23793.38128 60000.789123 78901.890236 1 12340000014520",
    paymentProofUrl: "/proofs/proof-025-3.pdf",
  },
  {
    installmentId: "inst-076",
    orderId: "ord-022",
    invoiceId: "inv-026",
    installmentNumber: 1,
    dueDate: "2024-12-10",
    amount: 26300.0,
    status: "pending",
    boletoUrl: "/boletos/bol-026-1.pdf",
    boletoBarcode: "23793.38128 60000.890234 78901.901345 2 12340000026300",
  },
  {
    installmentId: "inst-077",
    orderId: "ord-022",
    invoiceId: "inv-026",
    installmentNumber: 2,
    dueDate: "2025-01-10",
    amount: 26300.0,
    status: "pending",
    boletoUrl: "/boletos/bol-026-2.pdf",
    boletoBarcode: "23793.38128 60000.890234 78901.901346 3 12340000026300",
  },
  {
    installmentId: "inst-078",
    orderId: "ord-022",
    invoiceId: "inv-026",
    installmentNumber: 3,
    dueDate: "2025-02-10",
    amount: 26300.0,
    status: "pending",
    boletoUrl: "/boletos/bol-026-3.pdf",
    boletoBarcode: "23793.38128 60000.890234 78901.901347 4 12340000026300",
  },
  {
    installmentId: "inst-079",
    orderId: "ord-023",
    invoiceId: "inv-027",
    installmentNumber: 1,
    dueDate: "2024-04-10",
    amount: 17446.67,
    status: "paid",
    paymentDate: "2024-04-08",
    boletoUrl: "/boletos/bol-027-1.pdf",
    boletoBarcode: "23793.38128 60000.901345 78901.012456 5 12340000017446",
    paymentProofUrl: "/proofs/proof-027-1.pdf",
  },
  {
    installmentId: "inst-080",
    orderId: "ord-023",
    invoiceId: "inv-027",
    installmentNumber: 2,
    dueDate: "2024-05-10",
    amount: 17446.67,
    status: "paid",
    paymentDate: "2024-05-09",
    boletoUrl: "/boletos/bol-027-2.pdf",
    boletoBarcode: "23793.38128 60000.901345 78901.012457 6 12340000017446",
    paymentProofUrl: "/proofs/proof-027-2.pdf",
  },
  {
    installmentId: "inst-081",
    orderId: "ord-023",
    invoiceId: "inv-027",
    installmentNumber: 3,
    dueDate: "2024-06-10",
    amount: 17446.66,
    status: "paid",
    paymentDate: "2024-06-07",
    boletoUrl: "/boletos/bol-027-3.pdf",
    boletoBarcode: "23793.38128 60000.901345 78901.012458 7 12340000017446",
    paymentProofUrl: "/proofs/proof-027-3.pdf",
  },
  {
    installmentId: "inst-082",
    orderId: "ord-024",
    invoiceId: "inv-028",
    installmentNumber: 1,
    dueDate: "2024-07-25",
    amount: 29890.0,
    status: "paid",
    paymentDate: "2024-07-23",
    boletoUrl: "/boletos/bol-028-1.pdf",
    boletoBarcode: "23793.38128 60000.012456 78901.123567 8 12340000029890",
    paymentProofUrl: "/proofs/proof-028-1.pdf",
  },
  {
    installmentId: "inst-083",
    orderId: "ord-024",
    invoiceId: "inv-028",
    installmentNumber: 2,
    dueDate: "2024-08-25",
    amount: 29890.0,
    status: "paid",
    paymentDate: "2024-08-22",
    boletoUrl: "/boletos/bol-028-2.pdf",
    boletoBarcode: "23793.38128 60000.012456 78901.123568 9 12340000029890",
    paymentProofUrl: "/proofs/proof-028-2.pdf",
  },
  {
    installmentId: "inst-084",
    orderId: "ord-024",
    invoiceId: "inv-028",
    installmentNumber: 3,
    dueDate: "2024-09-25",
    amount: 29890.0,
    status: "pending",
    boletoUrl: "/boletos/bol-028-3.pdf",
    boletoBarcode: "23793.38128 60000.012456 78901.123569 1 12340000029890",
  },
  {
    installmentId: "inst-085",
    orderId: "ord-025",
    invoiceId: "inv-029",
    installmentNumber: 1,
    dueDate: "2024-11-05",
    amount: 44853.33,
    status: "pending",
    boletoUrl: "/boletos/bol-029-1.pdf",
    boletoBarcode: "23793.38128 60000.123567 78901.234678 2 12340000044853",
  },
  {
    installmentId: "inst-086",
    orderId: "ord-025",
    invoiceId: "inv-029",
    installmentNumber: 2,
    dueDate: "2024-12-05",
    amount: 44853.33,
    status: "pending",
    boletoUrl: "/boletos/bol-029-2.pdf",
    boletoBarcode: "23793.38128 60000.123567 78901.234679 3 12340000044853",
  },
  {
    installmentId: "inst-087",
    orderId: "ord-025",
    invoiceId: "inv-029",
    installmentNumber: 3,
    dueDate: "2025-01-05",
    amount: 44853.34,
    status: "pending",
    boletoUrl: "/boletos/bol-029-3.pdf",
    boletoBarcode: "23793.38128 60000.123567 78901.234680 4 12340000044853",
  },
  {
    installmentId: "inst-088",
    orderId: "ord-026",
    invoiceId: "inv-030",
    installmentNumber: 1,
    dueDate: "2025-01-01",
    amount: 25630.0,
    status: "pending",
    boletoUrl: "/boletos/bol-030-1.pdf",
    boletoBarcode: "23793.38128 60000.234678 78901.345789 5 12340000025630",
  },
  {
    installmentId: "inst-089",
    orderId: "ord-026",
    invoiceId: "inv-030",
    installmentNumber: 2,
    dueDate: "2025-02-01",
    amount: 25630.0,
    status: "pending",
    boletoUrl: "/boletos/bol-030-2.pdf",
    boletoBarcode: "23793.38128 60000.234678 78901.345790 6 12340000025630",
  },
  {
    installmentId: "inst-090",
    orderId: "ord-026",
    invoiceId: "inv-030",
    installmentNumber: 3,
    dueDate: "2025-03-01",
    amount: 25630.0,
    status: "pending",
    boletoUrl: "/boletos/bol-030-3.pdf",
    boletoBarcode: "23793.38128 60000.234678 78901.345791 7 12340000025630",
  },
  {
    installmentId: "inst-091",
    orderId: "ord-027",
    invoiceId: "inv-031",
    installmentNumber: 1,
    dueDate: "2025-01-20",
    amount: 37446.67,
    status: "pending",
    boletoUrl: "/boletos/bol-031-1.pdf",
    boletoBarcode: "23793.38128 60000.345789 78901.456890 8 12340000037446",
  },
  {
    installmentId: "inst-092",
    orderId: "ord-027",
    invoiceId: "inv-031",
    installmentNumber: 2,
    dueDate: "2025-02-20",
    amount: 37446.67,
    status: "pending",
    boletoUrl: "/boletos/bol-031-2.pdf",
    boletoBarcode: "23793.38128 60000.345789 78901.456891 9 12340000037446",
  },
  {
    installmentId: "inst-093",
    orderId: "ord-027",
    invoiceId: "inv-031",
    installmentNumber: 3,
    dueDate: "2025-03-20",
    amount: 37446.66,
    status: "pending",
    boletoUrl: "/boletos/bol-031-3.pdf",
    boletoBarcode: "23793.38128 60000.345789 78901.456892 1 12340000037446",
  },
  {
    installmentId: "inst-094",
    orderId: "ord-028",
    invoiceId: "inv-032",
    installmentNumber: 1,
    dueDate: "2025-02-15",
    amount: 32920.0,
    status: "pending",
    boletoUrl: "/boletos/bol-032-1.pdf",
    boletoBarcode: "23793.38128 60000.456890 78901.567901 2 12340000032920",
  },
  {
    installmentId: "inst-095",
    orderId: "ord-028",
    invoiceId: "inv-032",
    installmentNumber: 2,
    dueDate: "2025-03-15",
    amount: 32920.0,
    status: "pending",
    boletoUrl: "/boletos/bol-032-2.pdf",
    boletoBarcode: "23793.38128 60000.456890 78901.567902 3 12340000032920",
  },
  {
    installmentId: "inst-096",
    orderId: "ord-028",
    invoiceId: "inv-032",
    installmentNumber: 3,
    dueDate: "2025-04-15",
    amount: 32920.0,
    status: "pending",
    boletoUrl: "/boletos/bol-032-3.pdf",
    boletoBarcode: "23793.38128 60000.456890 78901.567903 4 12340000032920",
  },
  {
    installmentId: "inst-097",
    orderId: "ord-029",
    invoiceId: "inv-033",
    installmentNumber: 1,
    dueDate: "2025-02-05",
    amount: 22483.33,
    status: "pending",
    boletoUrl: "/boletos/bol-033-1.pdf",
    boletoBarcode: "23793.38128 60000.567901 78901.678012 5 12340000022483",
  },
  {
    installmentId: "inst-098",
    orderId: "ord-029",
    invoiceId: "inv-033",
    installmentNumber: 2,
    dueDate: "2025-03-05",
    amount: 22483.33,
    status: "pending",
    boletoUrl: "/boletos/bol-033-2.pdf",
    boletoBarcode: "23793.38128 60000.567901 78901.678013 6 12340000022483",
  },
  {
    installmentId: "inst-099",
    orderId: "ord-029",
    invoiceId: "inv-033",
    installmentNumber: 3,
    dueDate: "2025-04-05",
    amount: 22483.34,
    status: "pending",
    boletoUrl: "/boletos/bol-033-3.pdf",
    boletoBarcode: "23793.38128 60000.567901 78901.678014 7 12340000022483",
  },
  {
    installmentId: "inst-100",
    orderId: "ord-030",
    invoiceId: "inv-034",
    installmentNumber: 1,
    dueDate: "2025-02-10",
    amount: 29780.0,
    status: "pending",
    boletoUrl: "/boletos/bol-034-1.pdf",
    boletoBarcode: "23793.38128 60000.678012 78901.789123 8 12340000029780",
  },
  {
    installmentId: "inst-101",
    orderId: "ord-030",
    invoiceId: "inv-034",
    installmentNumber: 2,
    dueDate: "2025-03-10",
    amount: 29780.0,
    status: "pending",
    boletoUrl: "/boletos/bol-034-2.pdf",
    boletoBarcode: "23793.38128 60000.678012 78901.789124 9 12340000029780",
  },
  {
    installmentId: "inst-102",
    orderId: "ord-030",
    invoiceId: "inv-034",
    installmentNumber: 3,
    dueDate: "2025-04-10",
    amount: 29780.0,
    status: "pending",
    boletoUrl: "/boletos/bol-034-3.pdf",
    boletoBarcode: "23793.38128 60000.678012 78901.789125 1 12340000029780",
  },
  // Additional 48 installments for split invoices (inst-103 to inst-150)
  {
    installmentId: "inst-103",
    orderId: "ord-011",
    invoiceId: "inv-035",
    installmentNumber: 1,
    dueDate: "2024-04-15",
    amount: 4322.22,
    status: "paid",
    paymentDate: "2024-04-14",
    boletoUrl: "/boletos/bol-035-1.pdf",
    boletoBarcode: "23793.38128 60000.789123 78901.890234 2 12340000004322",
    paymentProofUrl: "/proofs/proof-035-1.pdf",
  },
  {
    installmentId: "inst-104",
    orderId: "ord-011",
    invoiceId: "inv-035",
    installmentNumber: 2,
    dueDate: "2024-05-15",
    amount: 4322.22,
    status: "paid",
    paymentDate: "2024-05-13",
    boletoUrl: "/boletos/bol-035-2.pdf",
    boletoBarcode: "23793.38128 60000.789123 78901.890235 3 12340000004322",
    paymentProofUrl: "/proofs/proof-035-2.pdf",
  },
  {
    installmentId: "inst-105",
    orderId: "ord-011",
    invoiceId: "inv-035",
    installmentNumber: 3,
    dueDate: "2024-06-15",
    amount: 4322.23,
    status: "paid",
    paymentDate: "2024-06-12",
    boletoUrl: "/boletos/bol-035-3.pdf",
    boletoBarcode: "23793.38128 60000.789123 78901.890236 4 12340000004322",
    paymentProofUrl: "/proofs/proof-035-3.pdf",
  },
  {
    installmentId: "inst-106",
    orderId: "ord-012",
    invoiceId: "inv-036",
    installmentNumber: 1,
    dueDate: "2024-07-20",
    amount: 8037.83,
    status: "pending",
    boletoUrl: "/boletos/bol-036-1.pdf",
    boletoBarcode: "23793.38128 60000.890234 78901.901345 5 12340000008037",
  },
  {
    installmentId: "inst-107",
    orderId: "ord-012",
    invoiceId: "inv-036",
    installmentNumber: 2,
    dueDate: "2024-08-20",
    amount: 8037.83,
    status: "pending",
    boletoUrl: "/boletos/bol-036-2.pdf",
    boletoBarcode: "23793.38128 60000.890234 78901.901346 6 12340000008037",
  },
  {
    installmentId: "inst-108",
    orderId: "ord-012",
    invoiceId: "inv-036",
    installmentNumber: 3,
    dueDate: "2024-09-20",
    amount: 8037.84,
    status: "pending",
    boletoUrl: "/boletos/bol-036-3.pdf",
    boletoBarcode: "23793.38128 60000.890234 78901.901347 7 12340000008037",
  },
  {
    installmentId: "inst-109",
    orderId: "ord-013",
    invoiceId: "inv-037",
    installmentNumber: 1,
    dueDate: "2024-11-05",
    amount: 6098.89,
    status: "pending",
    boletoUrl: "/boletos/bol-037-1.pdf",
    boletoBarcode: "23793.38128 60000.901345 78901.012456 8 12340000006098",
  },
  {
    installmentId: "inst-110",
    orderId: "ord-013",
    invoiceId: "inv-037",
    installmentNumber: 2,
    dueDate: "2024-12-05",
    amount: 6098.89,
    status: "pending",
    boletoUrl: "/boletos/bol-037-2.pdf",
    boletoBarcode: "23793.38128 60000.901345 78901.012457 9 12340000006098",
  },
  {
    installmentId: "inst-111",
    orderId: "ord-013",
    invoiceId: "inv-037",
    installmentNumber: 3,
    dueDate: "2025-01-05",
    amount: 6098.89,
    status: "pending",
    boletoUrl: "/boletos/bol-037-3.pdf",
    boletoBarcode: "23793.38128 60000.901345 78901.012458 1 12340000006098",
  },
  {
    installmentId: "inst-112",
    orderId: "ord-014",
    invoiceId: "inv-038",
    installmentNumber: 1,
    dueDate: "2024-05-05",
    amount: 10753.33,
    status: "paid",
    paymentDate: "2024-05-03",
    boletoUrl: "/boletos/bol-038-1.pdf",
    boletoBarcode: "23793.38128 60000.012456 78901.123567 2 12340000010753",
    paymentProofUrl: "/proofs/proof-038-1.pdf",
  },
  {
    installmentId: "inst-113",
    orderId: "ord-014",
    invoiceId: "inv-038",
    installmentNumber: 2,
    dueDate: "2024-06-05",
    amount: 10753.33,
    status: "paid",
    paymentDate: "2024-06-04",
    boletoUrl: "/boletos/bol-038-2.pdf",
    boletoBarcode: "23793.38128 60000.012456 78901.123568 3 12340000010753",
    paymentProofUrl: "/proofs/proof-038-2.pdf",
  },
  {
    installmentId: "inst-114",
    orderId: "ord-014",
    invoiceId: "inv-038",
    installmentNumber: 3,
    dueDate: "2024-07-05",
    amount: 10753.34,
    status: "paid",
    paymentDate: "2024-07-03",
    boletoUrl: "/boletos/bol-038-3.pdf",
    boletoBarcode: "23793.38128 60000.012456 78901.123569 4 12340000010753",
    paymentProofUrl: "/proofs/proof-038-3.pdf",
  },
  {
    installmentId: "inst-115",
    orderId: "ord-015",
    invoiceId: "inv-039",
    installmentNumber: 1,
    dueDate: "2024-08-15",
    amount: 9518.89,
    status: "paid",
    paymentDate: "2024-08-14",
    boletoUrl: "/boletos/bol-039-1.pdf",
    boletoBarcode: "23793.38128 60000.123567 78901.234678 5 12340000009518",
    paymentProofUrl: "/proofs/proof-039-1.pdf",
  },
  {
    installmentId: "inst-116",
    orderId: "ord-015",
    invoiceId: "inv-039",
    installmentNumber: 2,
    dueDate: "2024-09-15",
    amount: 9518.89,
    status: "pending",
    boletoUrl: "/boletos/bol-039-2.pdf",
    boletoBarcode: "23793.38128 60000.123567 78901.234679 6 12340000009518",
  },
  {
    installmentId: "inst-117",
    orderId: "ord-015",
    invoiceId: "inv-039",
    installmentNumber: 3,
    dueDate: "2024-10-15",
    amount: 9518.89,
    status: "pending",
    boletoUrl: "/boletos/bol-039-3.pdf",
    boletoBarcode: "23793.38128 60000.123567 78901.234680 7 12340000009518",
  },
  {
    installmentId: "inst-118",
    orderId: "ord-016",
    invoiceId: "inv-040",
    installmentNumber: 1,
    dueDate: "2024-12-15",
    amount: 13716.67,
    status: "pending",
    boletoUrl: "/boletos/bol-040-1.pdf",
    boletoBarcode: "23793.38128 60000.234678 78901.345789 8 12340000013716",
  },
  {
    installmentId: "inst-119",
    orderId: "ord-016",
    invoiceId: "inv-040",
    installmentNumber: 2,
    dueDate: "2025-01-15",
    amount: 13716.67,
    status: "pending",
    boletoUrl: "/boletos/bol-040-2.pdf",
    boletoBarcode: "23793.38128 60000.234678 78901.345790 9 12340000013716",
  },
  {
    installmentId: "inst-120",
    orderId: "ord-016",
    invoiceId: "inv-040",
    installmentNumber: 3,
    dueDate: "2025-02-15",
    amount: 13716.66,
    status: "pending",
    boletoUrl: "/boletos/bol-040-3.pdf",
    boletoBarcode: "23793.38128 60000.234678 78901.345791 1 12340000013716",
  },
  {
    installmentId: "inst-121",
    orderId: "ord-017",
    invoiceId: "inv-041",
    installmentNumber: 1,
    dueDate: "2024-06-25",
    amount: 7543.33,
    status: "paid",
    paymentDate: "2024-06-23",
    boletoUrl: "/boletos/bol-041-1.pdf",
    boletoBarcode: "23793.38128 60000.345789 78901.456890 2 12340000007543",
    paymentProofUrl: "/proofs/proof-041-1.pdf",
  },
  {
    installmentId: "inst-122",
    orderId: "ord-017",
    invoiceId: "inv-041",
    installmentNumber: 2,
    dueDate: "2024-07-25",
    amount: 7543.33,
    status: "paid",
    paymentDate: "2024-07-24",
    boletoUrl: "/boletos/bol-041-2.pdf",
    boletoBarcode: "23793.38128 60000.345789 78901.456891 3 12340000007543",
    paymentProofUrl: "/proofs/proof-041-2.pdf",
  },
  {
    installmentId: "inst-123",
    orderId: "ord-017",
    invoiceId: "inv-041",
    installmentNumber: 3,
    dueDate: "2024-08-25",
    amount: 7543.34,
    status: "paid",
    paymentDate: "2024-08-23",
    boletoUrl: "/boletos/bol-041-3.pdf",
    boletoBarcode: "23793.38128 60000.345789 78901.456892 4 12340000007543",
    paymentProofUrl: "/proofs/proof-041-3.pdf",
  },
  {
    installmentId: "inst-124",
    orderId: "ord-018",
    invoiceId: "inv-042",
    installmentNumber: 1,
    dueDate: "2024-10-01",
    amount: 10973.33,
    status: "pending",
    boletoUrl: "/boletos/bol-042-1.pdf",
    boletoBarcode: "23793.38128 60000.456890 78901.567901 5 12340000010973",
  },
  {
    installmentId: "inst-125",
    orderId: "ord-018",
    invoiceId: "inv-042",
    installmentNumber: 2,
    dueDate: "2024-11-01",
    amount: 10973.33,
    status: "pending",
    boletoUrl: "/boletos/bol-042-2.pdf",
    boletoBarcode: "23793.38128 60000.456890 78901.567902 6 12340000010973",
  },
  {
    installmentId: "inst-126",
    orderId: "ord-018",
    invoiceId: "inv-042",
    installmentNumber: 3,
    dueDate: "2024-12-01",
    amount: 10973.34,
    status: "pending",
    boletoUrl: "/boletos/bol-042-3.pdf",
    boletoBarcode: "23793.38128 60000.456890 78901.567903 7 12340000010973",
  },
  {
    installmentId: "inst-127",
    orderId: "ord-019",
    invoiceId: "inv-043",
    installmentNumber: 1,
    dueDate: "2025-01-10",
    amount: 16185.56,
    status: "pending",
    boletoUrl: "/boletos/bol-043-1.pdf",
    boletoBarcode: "23793.38128 60000.567901 78901.678012 8 12340000016185",
  },
  {
    installmentId: "inst-128",
    orderId: "ord-019",
    invoiceId: "inv-043",
    installmentNumber: 2,
    dueDate: "2025-02-10",
    amount: 16185.56,
    status: "pending",
    boletoUrl: "/boletos/bol-043-2.pdf",
    boletoBarcode: "23793.38128 60000.567901 78901.678013 9 12340000016185",
  },
  {
    installmentId: "inst-129",
    orderId: "ord-019",
    invoiceId: "inv-043",
    installmentNumber: 3,
    dueDate: "2025-03-10",
    amount: 16185.55,
    status: "pending",
    boletoUrl: "/boletos/bol-043-3.pdf",
    boletoBarcode: "23793.38128 60000.567901 78901.678014 1 12340000016185",
  },
  {
    installmentId: "inst-130",
    orderId: "ord-020",
    invoiceId: "inv-044",
    installmentNumber: 1,
    dueDate: "2024-06-01",
    amount: 4840.0,
    status: "paid",
    paymentDate: "2024-05-30",
    boletoUrl: "/boletos/bol-044-1.pdf",
    boletoBarcode: "23793.38128 60000.678012 78901.789123 2 12340000004840",
    paymentProofUrl: "/proofs/proof-044-1.pdf",
  },
  {
    installmentId: "inst-131",
    orderId: "ord-020",
    invoiceId: "inv-044",
    installmentNumber: 2,
    dueDate: "2024-07-01",
    amount: 4840.0,
    status: "paid",
    paymentDate: "2024-06-28",
    boletoUrl: "/boletos/bol-044-2.pdf",
    boletoBarcode: "23793.38128 60000.678012 78901.789124 3 12340000004840",
    paymentProofUrl: "/proofs/proof-044-2.pdf",
  },
  {
    installmentId: "inst-132",
    orderId: "ord-020",
    invoiceId: "inv-044",
    installmentNumber: 3,
    dueDate: "2024-08-01",
    amount: 4840.0,
    status: "paid",
    paymentDate: "2024-07-30",
    boletoUrl: "/boletos/bol-044-3.pdf",
    boletoBarcode: "23793.38128 60000.678012 78901.789125 4 12340000004840",
    paymentProofUrl: "/proofs/proof-044-3.pdf",
  },
  {
    installmentId: "inst-133",
    orderId: "ord-022",
    invoiceId: "inv-045",
    installmentNumber: 1,
    dueDate: "2024-12-25",
    amount: 8766.67,
    status: "pending",
    boletoUrl: "/boletos/bol-045-1.pdf",
    boletoBarcode: "23793.38128 60000.789123 78901.890234 5 12340000008766",
  },
  {
    installmentId: "inst-134",
    orderId: "ord-022",
    invoiceId: "inv-045",
    installmentNumber: 2,
    dueDate: "2025-01-25",
    amount: 8766.67,
    status: "pending",
    boletoUrl: "/boletos/bol-045-2.pdf",
    boletoBarcode: "23793.38128 60000.789123 78901.890235 6 12340000008766",
  },
  {
    installmentId: "inst-135",
    orderId: "ord-022",
    invoiceId: "inv-045",
    installmentNumber: 3,
    dueDate: "2025-02-25",
    amount: 8766.66,
    status: "pending",
    boletoUrl: "/boletos/bol-045-3.pdf",
    boletoBarcode: "23793.38128 60000.789123 78901.890236 7 12340000008766",
  },
  {
    installmentId: "inst-136",
    orderId: "ord-023",
    invoiceId: "inv-046",
    installmentNumber: 1,
    dueDate: "2024-04-25",
    amount: 5815.56,
    status: "paid",
    paymentDate: "2024-04-24",
    boletoUrl: "/boletos/bol-046-1.pdf",
    boletoBarcode: "23793.38128 60000.890234 78901.901345 8 12340000005815",
    paymentProofUrl: "/proofs/proof-046-1.pdf",
  },
  {
    installmentId: "inst-137",
    orderId: "ord-023",
    invoiceId: "inv-046",
    installmentNumber: 2,
    dueDate: "2024-05-25",
    amount: 5815.56,
    status: "paid",
    paymentDate: "2024-05-23",
    boletoUrl: "/boletos/bol-046-2.pdf",
    boletoBarcode: "23793.38128 60000.890234 78901.901346 9 12340000005815",
    paymentProofUrl: "/proofs/proof-046-2.pdf",
  },
  {
    installmentId: "inst-138",
    orderId: "ord-023",
    invoiceId: "inv-046",
    installmentNumber: 3,
    dueDate: "2024-06-25",
    amount: 5815.55,
    status: "paid",
    paymentDate: "2024-06-23",
    boletoUrl: "/boletos/bol-046-3.pdf",
    boletoBarcode: "23793.38128 60000.890234 78901.901347 1 12340000005815",
    paymentProofUrl: "/proofs/proof-046-3.pdf",
  },
  {
    installmentId: "inst-139",
    orderId: "ord-024",
    invoiceId: "inv-047",
    installmentNumber: 1,
    dueDate: "2024-08-10",
    amount: 9963.33,
    status: "paid",
    paymentDate: "2024-08-08",
    boletoUrl: "/boletos/bol-047-1.pdf",
    boletoBarcode: "23793.38128 60000.901345 78901.012456 2 12340000009963",
    paymentProofUrl: "/proofs/proof-047-1.pdf",
  },
  {
    installmentId: "inst-140",
    orderId: "ord-024",
    invoiceId: "inv-047",
    installmentNumber: 2,
    dueDate: "2024-09-10",
    amount: 9963.33,
    status: "paid",
    paymentDate: "2024-09-09",
    boletoUrl: "/boletos/bol-047-2.pdf",
    boletoBarcode: "23793.38128 60000.901345 78901.012457 3 12340000009963",
    paymentProofUrl: "/proofs/proof-047-2.pdf",
  },
  {
    installmentId: "inst-141",
    orderId: "ord-024",
    invoiceId: "inv-047",
    installmentNumber: 3,
    dueDate: "2024-10-10",
    amount: 9963.34,
    status: "pending",
    boletoUrl: "/boletos/bol-047-3.pdf",
    boletoBarcode: "23793.38128 60000.901345 78901.012458 4 12340000009963",
  },
  {
    installmentId: "inst-142",
    orderId: "ord-025",
    invoiceId: "inv-048",
    installmentNumber: 1,
    dueDate: "2024-11-20",
    amount: 14951.11,
    status: "pending",
    boletoUrl: "/boletos/bol-048-1.pdf",
    boletoBarcode: "23793.38128 60000.012456 78901.123567 5 12340000014951",
  },
  {
    installmentId: "inst-143",
    orderId: "ord-025",
    invoiceId: "inv-048",
    installmentNumber: 2,
    dueDate: "2024-12-20",
    amount: 14951.11,
    status: "pending",
    boletoUrl: "/boletos/bol-048-2.pdf",
    boletoBarcode: "23793.38128 60000.012456 78901.123568 6 12340000014951",
  },
  {
    installmentId: "inst-144",
    orderId: "ord-025",
    invoiceId: "inv-048",
    installmentNumber: 3,
    dueDate: "2025-01-20",
    amount: 14951.11,
    status: "pending",
    boletoUrl: "/boletos/bol-048-3.pdf",
    boletoBarcode: "23793.38128 60000.012456 78901.123569 7 12340000014951",
  },
  {
    installmentId: "inst-145",
    orderId: "ord-026",
    invoiceId: "inv-049",
    installmentNumber: 1,
    dueDate: "2025-01-15",
    amount: 8543.33,
    status: "pending",
    boletoUrl: "/boletos/bol-049-1.pdf",
    boletoBarcode: "23793.38128 60000.123567 78901.234678 8 12340000008543",
  },
  {
    installmentId: "inst-146",
    orderId: "ord-026",
    invoiceId: "inv-049",
    installmentNumber: 2,
    dueDate: "2025-02-15",
    amount: 8543.33,
    status: "pending",
    boletoUrl: "/boletos/bol-049-2.pdf",
    boletoBarcode: "23793.38128 60000.123567 78901.234679 9 12340000008543",
  },
  {
    installmentId: "inst-147",
    orderId: "ord-026",
    invoiceId: "inv-049",
    installmentNumber: 3,
    dueDate: "2025-03-15",
    amount: 8543.34,
    status: "pending",
    boletoUrl: "/boletos/bol-049-3.pdf",
    boletoBarcode: "23793.38128 60000.123567 78901.234680 1 12340000008543",
  },
  {
    installmentId: "inst-148",
    orderId: "ord-027",
    invoiceId: "inv-050",
    installmentNumber: 1,
    dueDate: "2025-02-05",
    amount: 12482.22,
    status: "pending",
    boletoUrl: "/boletos/bol-050-1.pdf",
    boletoBarcode: "23793.38128 60000.234678 78901.345789 2 12340000012482",
  },
  {
    installmentId: "inst-149",
    orderId: "ord-027",
    invoiceId: "inv-050",
    installmentNumber: 2,
    dueDate: "2025-03-05",
    amount: 12482.22,
    status: "pending",
    boletoUrl: "/boletos/bol-050-2.pdf",
    boletoBarcode: "23793.38128 60000.234678 78901.345790 3 12340000012482",
  },
  {
    installmentId: "inst-150",
    orderId: "ord-027",
    invoiceId: "inv-050",
    installmentNumber: 3,
    dueDate: "2025-04-05",
    amount: 12482.23,
    status: "pending",
    boletoUrl: "/boletos/bol-050-3.pdf",
    boletoBarcode: "23793.38128 60000.234678 78901.345791 4 12340000012482",
  },
];

// Mock Activity Logs (160 activities across all clients)
export const mockActivityLogs: ActivityLog[] = [];

// Generate 20 activities per client (8 clients = 160 total)
const activityTypes: ActivityLog["actionType"][] = [
  "ORDER_CREATED",
  "ORDER_UPDATED",
  "PAYMENT_RECEIVED",
  "INVOICE_ISSUED",
  "PRODUCT_ADDED",
  "PRODUCT_EDITED",
  "PRODUCT_REMOVED",
  "ACCOUNT_UPDATED",
  "CONTACT_MADE",
];
const clients = mockUsers.filter((u) => u.role === "customer");

clients.forEach((client, clientIndex) => {
  for (let i = 0; i < 20; i++) {
    const daysAgo = i * 5 + clientIndex * 2;
    const date = new Date();
    date.setDate(date.getDate() - daysAgo);

    const actionType = activityTypes[i % activityTypes.length];
    mockActivityLogs.push({
      activityId: `act-${clientIndex}-${String(i).padStart(3, "0")}`,
      clientCode: client.clientCode,
      clientName: client.name,
      actionType,
      timestamp: date.toISOString(),
      description: `${actionType.replace(/_/g, " ")} - Cliente ${
        client.clientCode
      }`,
      actor: i % 3 === 0 ? "Sistema" : client.name,
      metadata: { index: i, clientIndex },
    });
  }
});

export const getActivityLogsByClient = (clientCode: string): ActivityLog[] => {
  return mockActivityLogs
    .filter((log) => log.clientCode === clientCode)
    .sort(
      (a, b) =>
        new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
    )
    .slice(0, 20);
};

// Helper functions for data filtering
export const getUserByCode = (clientCode: string): CustomerUser | undefined => {
  return mockUsers.find((user) => user.clientCode === clientCode);
};

export const getOrdersByClient = (clientCode: string): Order[] => {
  return mockOrders.filter((order) => order.clientCode === clientCode);
};

export const getInvoicesByOrder = (orderId: string): Invoice[] => {
  return mockInvoices.filter((invoice) => invoice.orderId === orderId);
};

export const getInstallmentsByInvoice = (invoiceId: string): Installment[] => {
  return mockInstallments.filter(
    (installment) => installment.invoiceId === invoiceId,
  );
};

export const getInstallmentsByOrder = (orderId: string): Installment[] => {
  return mockInstallments.filter(
    (installment) => installment.orderId === orderId,
  );
};

// Summary statistics functions
export const getClientSummary = (clientCode: string) => {
  const orders = getOrdersByClient(clientCode);
  const totalOrders = orders.length;
  const totalAmount = orders.reduce((sum, order) => sum + order.totalAmount, 0);

  const allInstallments = orders.flatMap((order) =>
    getInstallmentsByOrder(order.orderId),
  );
  const paidInstallments = allInstallments.filter(
    (inst) => inst.status === "paid",
  );
  const overdueInstallments = allInstallments.filter(
    (inst) => inst.status === "overdue",
  );
  const pendingInstallments = allInstallments.filter(
    (inst) => inst.status === "pending",
  );

  return {
    totalOrders,
    totalAmount,
    totalInstallments: allInstallments.length,
    paidAmount: paidInstallments.reduce((sum, inst) => sum + inst.amount, 0),
    overdueAmount: overdueInstallments.reduce(
      (sum, inst) => sum + inst.amount,
      0,
    ),
    pendingAmount: pendingInstallments.reduce(
      (sum, inst) => sum + inst.amount,
      0,
    ),
    paidCount: paidInstallments.length,
    overdueCount: overdueInstallments.length,
    pendingCount: pendingInstallments.length,
  };
};
